import {
  CoPilotAgentModelOverridesSchema,
  CoPilotAgentModelsConfigSchema,
  CoPilotRunModelSchema,
} from '@terra/shared-dto-chat-types';
import { z } from 'zod';
import {
  PaginationDefaults,
  PaginationQueryParamsSchema,
  PaginationResponseSchema,
  BooleanQueryParamDefaultFalseSchema,
  OptionalBooleanQueryParamSchema,
} from '@terra/shared-dto-types';
import { TraceStatusSchema } from '@terra/shared-schemas-ai';
import {
  EndpointSourceSchema,
  FindingSeveritySchema,
  FindingStatusSchema,
  SignalCategorySchema,
  SignalOriginSchema,
  SignalStageSchema,
  SignalStatusSchema,
} from '@terra/shared-schemas-orchestrator';
import {
  CategoryClassificationPassSchema,
  ContinualLearningRoutedActionSchema,
  FeedbackFailureAreaSchema,
  FeedbackFailureCategorySchema,
} from '@terra/shared-contracts-signal-feedback';
import { CreateTenantLicenseBodySchema } from './license.schemas';
import {
  TenantAdminAssignmentResultSchema,
  TenantAdminEmailsSchema,
} from './cockpit-tenant-admins.schema';
import {
  CompanyDomainSchema,
  optionalCompanyDomainSchema,
} from './company-domain.schema';
import {
  COPILOT_SKILL_CLIENT_SURFACES,
  COPILOT_SKILL_SECURITY_DOMAINS,
} from './co-pilot-skills-availability';
import {
  GetSkillResponseSchema,
  SkillStatusSchema,
  SkillSummarySchema,
} from '@terra/shared-contracts-ai-skills';

export { CoPilotAgentModelsConfigSchema };

export const TENANT_APP_STATUSES = [
  'Working',
  'Stuck',
  'Partially Working',
  'Done',
  'Hold',
  'Waiting Tracking',
] as const;

export const TENANT_PRIORITIES = ['Low', 'Medium', 'High', 'Hold'] as const;

export const TENANT_FREQUENCIES = [
  'Monthly',
  'Weekly',
  'Every 2 Months',
  'One-Time',
] as const;

export const TENANT_POV_STATUSES = [
  'portal-pov',
  'platform-pov',
  'done',
  'never-started',
] as const;

export type TenantPovStatus = (typeof TENANT_POV_STATUSES)[number];

export const TENANT_HIERARCHY_VALUES = ['sub-tenant', 'root'] as const;

export type TenantHierarchy = (typeof TENANT_HIERARCHY_VALUES)[number];

export const SubtenantParentTenantSchema = z.object({
  tenantId: z.string(),
  name: z.string(),
});

export const SubtenantParentTenantsResponseSchema = z.object({
  tenants: z.array(SubtenantParentTenantSchema),
});

export const TENANT_PICKER_DEFAULT_LIMIT = 100;
export const TENANT_PICKER_MAX_LIMIT = 200;

export const TenantPickerQuerySchema = z.object({
  search: z.string().max(200).optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(TENANT_PICKER_MAX_LIMIT)
    .optional()
    .prefault(TENANT_PICKER_DEFAULT_LIMIT),
});

export const TenantSummarySchema = z.object({
  tenantId: z.string(),
  name: z.string().nullish(),
  companyName: z.string().nullish(),
  parentTenantId: z.string().nullish(),
  applicationCount: z.number(),
  activeExecutions: z.number(),
  endpointCount: z.number().optional(),
  codeGraphGenerated: z.number().int().nonnegative().optional(),
  codeGraphLimit: z.number().int().nullable().optional(),
  cleanEndpointsCount: z.number().optional(),
  isCustomer: z.boolean().optional(),
  povStatus: z.enum(TENANT_POV_STATUSES).nullable(),
  priority: z.string().optional(),
  frequency: z.string().optional(),
  researcherEmail: z.email().nullish(),
  salesOwnerEmail: z.email().nullish(),
  csOwnerEmail: z.email().nullish(),
  expectedApplicationCount: z.int().min(0).nullish(),
  totalSignals: z.number(),
  totalFindings: z.number(),
  lastActivityAt: z.iso.datetime().nullable(),
  companyInfo: z
    .object({
      domain: z.string().optional(),
      industry: z.string().optional(),
      employeeCount: z.string().optional(),
      createdAt: z.iso.datetime().nullable(),
    })
    .optional(),
  totalExecutions: z.number().optional(),
  firstExecutionAt: z.iso.datetime().nullable().optional(),
  findingsByStatus: z
    .object({
      unresolved: z.number(),
      inProgress: z.number(),
      resolved: z.number(),
    })
    .optional(),
  onboarding: z
    .object({
      hasApplications: z.boolean(),
      hasCredentials: z.boolean(),
      hasVerifiedCredentials: z.boolean(),
      hasExecutions: z.boolean(),
      hasGitIntegration: z.boolean(),
      hasFindings: z.boolean(),
      credentialVerificationRate: z.number(),
      gitIntegrationRate: z.number(),
    })
    .optional(),
  connectedIntegrations: z
    .array(
      z.object({
        key: z.string(),
        name: z.string(),
        category: z.string(),
      }),
    )
    .optional(),
  documentationStats: z
    .object({
      docsCount: z.number(),
      apiSpecCount: z.number(),
      testReportCount: z.number(),
      knowledgeSourceCount: z.number(),
      appsWithDocs: z.number(),
      appsWithApiSpec: z.number(),
      appsWithTestReports: z.number(),
    })
    .optional(),
});

export const DashboardKpiSchema = z.object({
  totalTenants: z.number(),
  totalApplications: z.number(),
  activeExecutions: z.number(),
  totalSignals: z.number(),
  totalFindings: z.number(),
  criticalFindings: z.number(),
});

export const CustomerActionColumnSchema = z.enum([
  'findingsToPublish',
  'retestsNotAutoPublished',
  'blockedSignals',
  'potentialSignals',
  'lockedCredentials',
  'missingCodeGraph',
]);

export type CustomerActionColumn = z.infer<typeof CustomerActionColumnSchema>;

export const CustomerActionCountsSchema = z.object({
  findingsToPublish: z.number().int().nonnegative(),
  retestsNotAutoPublished: z.number().int().nonnegative(),
  blockedSignals: z.number().int().nonnegative(),
  potentialSignals: z.number().int().nonnegative(),
  lockedCredentials: z.number().int().nonnegative(),
  missingCodeGraph: z.number().int().nonnegative(),
});

export type CustomerActionCounts = z.infer<typeof CustomerActionCountsSchema>;

export const CustomerHealthAppKindSchema = z.enum(['Web', 'AI']);

export type CustomerHealthAppKind = z.infer<typeof CustomerHealthAppKindSchema>;

export const CustomerHealthAppRowSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string(),
  applicationId: z.string(),
  applicationName: z.string(),
  kind: CustomerHealthAppKindSchema,
  environment: z.string(),
  totalSignals: z.number().int().nonnegative(),
  potentialVulnerability: z.number().int().nonnegative(),
  disproven: z.number().int().nonnegative(),
  confirmed: z.number().int().nonnegative(),
  blocked: z.number().int().nonnegative(),
  duplicate: z.number().int().nonnegative(),
  unlabeled: z.number().int().nonnegative(),
  unpublishedFindings: z.number().int().nonnegative(),
  retestFindings: z.number().int().nonnegative(),
  validationHubDisproven: z.number().int().nonnegative().default(0),
  validationHubNotDisproven: z.number().int().nonnegative().default(0),
  lastManualPublishAt: z.iso.datetime().nullable().default(null),
  endpointCount: z.number().int().nonnegative().default(0),
  endpointsMissingCodeGraph: z.number().int().nonnegative().default(0),
  endpointCandidateCount: z.number().int().nonnegative().default(0),
  potentialSignals: z.number().int().nonnegative().default(0),
  lockedCredentials: z.number().int().nonnegative().default(0),
});

export type CustomerHealthAppRow = z.infer<typeof CustomerHealthAppRowSchema>;

export const TenantHealthSchema = z.object({
  tenantId: z.string(),
  name: z.string().optional(),
  healthScore: z.number().min(0).max(100),
  executionSuccessRate: z.number().min(0).max(100),
  signalToFindingRatio: z.number(),
  lastExecutionAt: z.iso.datetime().nullable(),
  applicationCount: z.number(),
  activeExecutions: z.number(),
  totalSignals: z.number(),
  totalFindings: z.number(),
  criticalFindings: z.number(),
  actions: CustomerActionCountsSchema,
  totalOpenActions: z.number().int().nonnegative(),
});

export const CustomerHealthQuerySchema = z.object({
  search: z.string().max(200).optional(),
  actionColumn: CustomerActionColumnSchema.optional(),
  priority: z.enum(TENANT_PRIORITIES).optional(),
  frequency: z.enum(TENANT_FREQUENCIES).optional(),
  limit: z.coerce.number().min(1).max(5_000).optional().prefault(500),
  offset: z.coerce.number().min(0).max(50_000).optional().prefault(0),
});

export const CustomerHealthResponseSchema = z.object({
  apps: z.array(CustomerHealthAppRowSchema),
  total: z.number(),
  totals: CustomerActionCountsSchema,
});

export const TrendDataPointSchema = z.object({
  date: z.string(),
  count: z.number(),
});

export const TenantTrendSchema = z.object({
  tenantId: z.string(),
  data: z.array(TrendDataPointSchema),
});

export const ExecutionStatsSchema = z.object({
  total: z.number(),
  byStatus: z.object({
    running: z.number(),
    completed: z.number(),
    failed: z.number(),
    paused: z.number(),
    aborted: z.number(),
  }),
});

export const SignalDistributionSchema = z.object({
  NEW: z.number(),
  IN_TESTING: z.number(),
  NEEDS_REVIEW: z.number(),
  TESTED: z.number(),
});

export const SignalCategoryDistributionSchema = z.record(
  z.string(),
  z.number(),
);

export const SignalStatusDistributionSchema = z.object({
  POTENTIAL_VULNERABILITY: z.number(),
  BLOCKED: z.number(),
  CONFIRMED_VULNERABILITY: z.number(),
  DISPROVEN_VULNERABILITY: z.number(),
  DUPLICATE: z.number(),
  PENDING_ENRICHMENT: z.number(),
  FAILED_ENRICHMENT: z.number(),
  UNKNOWN: z.number(),
});

const FindingsBySeveritySchema = z.object({
  critical: z.number(),
  high: z.number(),
  medium: z.number(),
  low: z.number(),
});

export const FindingsByStatusSchema = z.object({
  Unresolved: z.number(),
  Resolved: z.number(),
  FalsePositive: z.number(),
  WontFix: z.number(),
  InProgress: z.number(),
  Retest: z.number(),
});

export const FindingsStatsSchema = z.object({
  total: z.number(),
  critical: z.number(),
  high: z.number(),
  medium: z.number(),
  low: z.number(),
  published: FindingsBySeveritySchema,
  unpublished: FindingsBySeveritySchema,
  byStatus: FindingsByStatusSchema,
});

const FindingAgeBucketSchema = z.object({
  Critical: z.number(),
  High: z.number(),
  Medium: z.number(),
  Low: z.number(),
});

export const TenantFindingsByAgeSchema = z.object({
  '0-7days': FindingAgeBucketSchema,
  '8-14days': FindingAgeBucketSchema,
  '15-30days': FindingAgeBucketSchema,
  '30+days': FindingAgeBucketSchema,
});

export const AttackSurfaceFunnelSchema = z.object({
  prsScanned: z.number(),
  attackSurfaceCode: z.number(),
  reachableAttackSurface: z.number(),
  generatedSignals: z.number(),
});

export const MAX_EXPORT_TENANTS = 100;
export const MAX_EXPORT_ROWS = 10_000;

export const ExportRequestSchema = z.object({
  tenantIds: z.array(z.string()).max(MAX_EXPORT_TENANTS).optional(),
  dataTypes: z.array(z.enum(['executions', 'signals', 'findings', 'health'])),
  dateFrom: z.iso.datetime().optional(),
  dateTo: z.iso.datetime().optional(),
  format: z.enum(['csv', 'json']).default('json'),
});

export const PovEnvironmentStatusSchema = z.enum([
  'pending',
  'running',
  'succeeded',
  'failed',
  'destroyed',
  'expired',
]);

export const PovDeploymentTargetSchema = z.enum(['aws', 'local']);
const CreatePovDeploymentTargetSchema = z.literal('aws');

export const MAX_POV_STACK_NAME_LENGTH = 63;

export const PovDatabaseResetSourceSchema = z.enum(['manual', 'experiment']);

export const PovExecutionStepTypeSchema = z.enum([
  'validate_request',
  'create_pulumi_stack',
  'trigger_pulumi_up',
  'update_gitops',
  'trigger_deploy',
  'verify_route53',
  'schedule_teardown',
  'destroy_environment',
  'update_inbound_rules',
  'reset_database',
]);

export const PovExecutionStepStatusSchema = z.enum([
  'pending',
  'in_progress',
  'successful',
  'failed',
  'skipped',
]);

const MIN_AIGBANK_INBOUND_SOURCE_PREFIX = 8;

function isUnspecifiedSourceIpv4Cidr(cidr: string): boolean {
  const ipPart = cidr.split('/')[0] ?? '';
  const octets = ipPart.split('.');

  return (
    octets.length === 4 &&
    octets.every((octet) => octet.length > 0 && Number(octet) === 0)
  );
}

function getIpv4CidrPrefix(cidr: string): number | null {
  const prefixPart = cidr.split('/')[1];

  if (!prefixPart) {
    return null;
  }

  const prefix = Number(prefixPart);

  return Number.isInteger(prefix) ? prefix : null;
}

const Ipv4CidrValueSchema = z
  .string()
  .regex(
    /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)\/(?:3[0-2]|[12]?\d)$/,
    'clientVpnCidr must be an IPv4 address or CIDR block',
  )
  .refine(
    (value) => {
      if (isUnspecifiedSourceIpv4Cidr(value)) {
        return false;
      }

      const prefix = getIpv4CidrPrefix(value);

      return prefix !== null && prefix >= MIN_AIGBANK_INBOUND_SOURCE_PREFIX;
    },
    {
      error:
        'Unspecified source IP and overly broad CIDRs (/0-/7) are not allowed for AIGBank inbound access',
    },
  );

const Ipv4CidrSchema = z.preprocess((value) => {
  if (typeof value !== 'string') {
    return value;
  }

  const trimmedValue = value.trim();

  if (trimmedValue.length === 0) {
    return undefined;
  }

  return trimmedValue.includes('/') ? trimmedValue : `${trimmedValue}/32`;
}, Ipv4CidrValueSchema.optional());

const Ipv4CidrListSchema = z.preprocess((value) => {
  if (!Array.isArray(value)) {
    return value;
  }

  return value.map((item) => {
    if (typeof item !== 'string') {
      return item;
    }

    const trimmedItem = item.trim();

    return trimmedItem.includes('/') ? trimmedItem : `${trimmedItem}/32`;
  });
}, z.array(Ipv4CidrValueSchema).max(20).optional());

const OptionalTtlHoursSchema = z.preprocess(
  (value) => {
    if (typeof value === 'string' && value.trim().length === 0) {
      return undefined;
    }

    return value;
  },
  z.coerce
    .number()
    .int()
    .min(1)
    .max(24 * 90)
    .optional(),
);

export const CreatePovEnvironmentBodySchema = z.object({
  deploymentTarget: CreatePovDeploymentTargetSchema.default('aws'),
  clientName: z.string().trim().min(2).max(MAX_POV_STACK_NAME_LENGTH),
  stackName: z.string().trim().min(2).max(MAX_POV_STACK_NAME_LENGTH).optional(),
  zone: z
    .string()
    .trim()
    .min(3)
    .max(200)
    .regex(
      /^[a-zA-Z0-9][a-zA-Z0-9.-]*[a-zA-Z0-9]$/,
      'zone must be a valid DNS name containing only letters, digits, dots, and hyphens',
    )
    .optional(),
  ttlHours: OptionalTtlHoursSchema,
  teardownAt: z.iso.datetime().optional(),
  clientVpnCidr: Ipv4CidrSchema,
  additionalClientVpnCidrs: Ipv4CidrListSchema,
  dryRun: z.boolean().optional().default(false),
});

export const PovExecutionStepSchema = z.object({
  id: z.string(),
  stepType: PovExecutionStepTypeSchema,
  status: PovExecutionStepStatusSchema,
  message: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  startedAt: z.iso.datetime().nullable(),
  finishedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const PovEnvironmentSummarySchema = z.object({
  id: z.string(),
  deploymentTarget: PovDeploymentTargetSchema,
  clientName: z.string(),
  stackName: z.string(),
  zone: z.string(),
  awsProfile: z.string().nullable(),
  awsAccountId: z.string().nullable(),
  awsRegion: z.string().nullable(),
  ttlHours: z.number().nullable(),
  status: PovEnvironmentStatusSchema,
  requestedBy: z.string().nullable(),
  requestedByUserId: z.string().nullable(),
  clientVpnCidr: z.string().nullable(),
  additionalClientVpnCidrs: z.array(z.string()),
  message: z.string().nullable(),
  dryRun: z.boolean(),
  teardownAt: z.iso.datetime().nullable(),
  lastDatabaseResetAt: z.iso.datetime().nullable().optional(),
  lastDatabaseResetBy: z.string().nullable().optional(),
  lastDatabaseResetSource: PovDatabaseResetSourceSchema.nullable().optional(),
  lastDatabaseResetExperimentId: z.string().nullable().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const PovEnvironmentStatusResponseSchema = z.object({
  environment: PovEnvironmentSummarySchema,
  steps: z.array(PovExecutionStepSchema),
});

export const PovEnvironmentListQuerySchema = PaginationQueryParamsSchema.extend(
  {
    skip: z.coerce.number().int().min(0).optional().prefault(0),
    limit: z.coerce.number().int().min(1).max(500).optional().prefault(100),
  },
).pick({
  skip: true,
  limit: true,
});

export const PovEnvironmentListResponseSchema = z.object({
  environments: z.array(PovEnvironmentSummarySchema),
  ...PaginationResponseSchema.shape,
});

export const ExtendPovEnvironmentBodySchema = z.object({
  ttlHours: z.coerce
    .number()
    .int()
    .min(1)
    .max(24 * 90),
});

export const UpdatePovEnvironmentIngressBodySchema = z.object({
  additionalClientVpnCidrs: Ipv4CidrListSchema.transform(
    (value) => value ?? [],
  ),
});

export const DestroyPovEnvironmentBodySchema = z.object({
  reason: z.string().trim().max(1_000).optional(),
});

export const DestroyPovEnvironmentResponseSchema = z.object({
  accepted: z.boolean(),
  environmentId: z.string(),
});

export const CredentialDetailStatusSchema = z.enum([
  'PENDING',
  'VERIFYING',
  'VERIFIED',
  'FAILED',
  'DISABLED',
]);

export const CredentialDetailSchema = z.object({
  id: z.string(),
  username: z.string(),
  name: z.string().nullish(),
  status: CredentialDetailStatusSchema,
  lastAttemptAt: z.iso.datetime().nullable(),
  verifiedAt: z.iso.datetime().nullable(),
  lastError: z.string().nullable(),
});

export const CredentialHealthSchema = z.object({
  total: z.number(),
  verified: z.number(),
  failed: z.number(),
  pending: z.number(),
  disabled: z.number(),
  hasDifferentRoles: z.boolean(),
  items: z.array(CredentialDetailSchema).default([]),
});

export const GitRepositoryHealthSchema = z.object({
  name: z.string(),
  targetBranch: z.string().nullish(),
});

export const GitHealthSchema = z.object({
  hasIntegration: z.boolean(),
  repositoryCount: z.number(),
  providers: z.array(z.string()),
  repositories: z.array(GitRepositoryHealthSchema).default([]),
});

export const ExecutionHealthSchema = z.object({
  total: z.number(),
  completed: z.number(),
  failed: z.number(),
  successRate: z.number(),
  lastExecutionAt: z.iso.datetime().nullable(),
});

export const CrawlerHealthSchema = z.object({
  periodicEnabled: z.boolean(),
  hasEverRun: z.boolean(),
  lastSessionStatus: z.string().nullable(),
  lastSessionAt: z.iso.datetime().nullable(),
});

export const ApplicationDomainSchema = z.object({
  domain: z.string(),
  hasWaf: z.boolean().nullable(),
  isInTestingScope: z.boolean().nullable().optional(),
  isInFunctionalityScope: z.boolean().nullable().optional(),
});

export const AutoExecutionHealthSchema = z.object({
  smokeTestEnabled: z.boolean(),
  hasSchedule: z.boolean(),
  schedule: z
    .object({
      daysOfWeek: z.array(z.number().min(0).max(6)),
      startHour: z.number().min(0).max(23),
      endHour: z.number().min(0).max(24),
      timezone: z.string().nullable(),
    })
    .nullable(),
  adaptivePhase: z.string().nullable().optional(),
  adaptivePausedReason: z.string().nullable().optional(),
  adaptiveHasResumed: z.boolean().optional(),
});

export const SignalsCoverageSchema = z.object({
  total: z.number().int().nonnegative(),
  disproven: z.number().int().nonnegative(),
  confirmed: z.number().int().nonnegative(),
  coverageRate: z.number().int().min(0).max(100),
  tested: z.number().int().nonnegative(),
  testingCompletionRate: z.number().int().min(0).max(100),
});

export type SignalsCoverage = z.infer<typeof SignalsCoverageSchema>;

export const EndpointMappingSchema = z.object({
  total: z.number(),
  endpointCount: z.number(),
  webpageCount: z.number(),
  withoutCodeGraph: z.number(),
});

export const SignalFunnelSchema = z.object({
  discovered: z.number(),
  analyzed: z.number(),
  flagged: z.number(),
  confirmed: z.number(),
});

export const ApplicationKindSchema = z.enum([
  'WEB_APPLICATION',
  'AI_APPLICATION',
]);

export const FpTargetTypeSchema = z.enum([
  'WEB_APPLICATION',
  'AI_APPLICATION',
  'EXTERNAL_NETWORK',
  'INTERNAL_NETWORK',
]);

export const UpdateAppTorchEnabledBodySchema = z.object({
  torchEnabled: z.boolean().nullable(),
});

export const AppTorchEnabledResponseSchema = z.object({
  applicationKind: ApplicationKindSchema,
  torchEnabled: z.boolean().nullable(),
  effectiveTorchEnabled: z.boolean(),
  tenantDefaultTorchEnabled: z.boolean(),
});

export const CockpitScheduledReportFrequencySchema = z.enum([
  'WEEKLY',
  'MONTHLY',
  'QUARTERLY',
  'SEMI_ANNUAL',
  'YEARLY',
]);

export const CockpitScheduledReportRuleSchema = z.object({
  id: z.string(),
  frequency: CockpitScheduledReportFrequencySchema,
  daysOfWeek: z.array(z.number().int()).default([]),
  dayOfMonth: z.number().int().nullable().optional(),
  lastDayOfMonth: z.boolean().nullable().optional(),
  fiscalYearStartMonth: z.number().int().nullable().optional(),
  yearlyMonth: z.number().int().nullable().optional(),
  yearlyDay: z.number().int().nullable().optional(),
  hour: z.number().int(),
  timezone: z.string(),
  lastRunAt: z.coerce.date().nullable().optional(),
  nextRunAt: z.coerce.date().nullable().optional(),
});

export type CockpitScheduledReportFrequency = z.infer<
  typeof CockpitScheduledReportFrequencySchema
>;
export type CockpitScheduledReportRule = z.infer<
  typeof CockpitScheduledReportRuleSchema
>;

export const AppHealthStatusSchema = z.object({
  applicationId: z.string(),
  applicationName: z.string(),
  applicationUrl: z.string(),
  applicationKind: ApplicationKindSchema,
  torchEnabled: z.boolean().nullable(),
  effectiveTorchEnabled: z.boolean(),
  tenantDefaultTorchEnabled: z.boolean(),
  createdAt: z.iso.datetime(),
  nextPentestReportDate: z.string().nullable(),
  scheduledReportsEnabled: z.boolean(),
  scheduledReportRules: z.array(CockpitScheduledReportRuleSchema),
  nextScheduledReportAt: z.string().datetime().nullable(),
  activeScheduledReportRuleCount: z.number().int().min(0),
  overallStatus: z.enum(['healthy', 'warning', 'critical', 'not_configured']),
  hasWaf: z.boolean().nullable(),
  isInternalApp: z.boolean(),
  hasInternalConnector: z.boolean(),
  isBackendOnly: z.boolean(),
  hasApiDocumentation: z.boolean(),
  domains: z.array(ApplicationDomainSchema),
  credentials: CredentialHealthSchema,
  git: GitHealthSchema,
  executions: ExecutionHealthSchema,
  crawler: CrawlerHealthSchema,
  autoExecution: AutoExecutionHealthSchema,
  findings: FindingsStatsSchema,
  signals: SignalsCoverageSchema,
  signalFunnel: SignalFunnelSchema,
  endpoints: EndpointMappingSchema,
});

export type AppHealthStatus = z.infer<typeof AppHealthStatusSchema>;

export const TenantHealthDetailSchema = z.object({
  tenantId: z.string(),
  name: z.string().optional(),
  summary: z.object({
    totalApps: z.number(),
    appsWithVerifiedCreds: z.number(),
    appsWithGitIntegration: z.number(),
    appsWithAutoExecution: z.number(),
    appsWithCrawler: z.number(),
    adaptivePausedCount: z.number().optional(),
    adaptiveVerifyingCount: z.number().optional(),
    adaptiveResumedCount: z.number().optional(),
    overallHealthy: z.number(),
    overallWarning: z.number(),
    overallCritical: z.number(),
    overallNotConfigured: z.number(),
  }),
  apps: z.array(AppHealthStatusSchema),
});

export const ApplicationLookupItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  tenantId: z.string().nullable(),
});

export const TraceListItemSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  tenantName: z.string().nullish(),
  applicationId: z.string().nullable(),
  applicationName: z.string().nullish(),
  threadId: z.string(),
  name: z.string(),
  status: TraceStatusSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  durationMs: z.number().nullable(),
  totalCostUsd: z.number().nullable(),
});

export const TraceListQuerySchema = z.object({
  status: TraceStatusSchema.optional(),
  agentName: z.enum(['neo']).optional(),
  search: z.string().max(200).optional(),
  tenantId: z.string().optional(),
  applicationId: z.string().optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
  limit: z.coerce.number().min(1).max(500).optional().prefault(50),
  offset: z.coerce.number().min(0).max(50_000).optional().prefault(0),
  sortBy: z.enum(['createdAt', 'updatedAt']).optional().default('updatedAt'),
  sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
});

export const TraceStatusCountsSchema = z.object({
  running: z.number(),
  completed: z.number(),
  failed: z.number(),
  paused: z.number(),
  aborted: z.number(),
  blocked: z.number(),
});

export const DateRangeQuerySchema = z.object({
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
});

export { CompanyDomainSchema } from './company-domain.schema';

export const CreateTenantBodySchema = z
  .object({
    name: z.string().min(1),
    tenantId: z.string().min(1).optional(),
    companyDomain: optionalCompanyDomainSchema,
    tenantHierarchy: z.enum(TENANT_HIERARCHY_VALUES).default('sub-tenant'),
    parentTenantId: z.string().min(1).optional(),
    isCustomer: z.boolean(),
    povStatus: z.enum(TENANT_POV_STATUSES).nullable().optional(),
    license: CreateTenantLicenseBodySchema,
    featureFlags: z
      .record(z.string(), z.union([z.boolean(), z.number(), z.null()]))
      .optional(),
    adminEmails: TenantAdminEmailsSchema.optional(),
    createAcademySubtenant: z.boolean().optional().default(false),
  })
  .superRefine((data, ctx) => {
    if (data.isCustomer && data.povStatus != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Customer tenants cannot have a POV status',
        path: ['povStatus'],
      });
    }

    if (
      (data.adminEmails?.length ?? 0) > 0 &&
      data.tenantHierarchy !== 'root'
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Tenant admins can only be assigned when creating a root tenant',
        path: ['adminEmails'],
      });
    }

    if (data.tenantHierarchy === 'sub-tenant' && !data.parentTenantId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Parent tenant is required when creating a sub-tenant',
        path: ['parentTenantId'],
      });
    }

    if (data.parentTenantId && data.tenantHierarchy !== 'sub-tenant') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Parent tenant can only be set when creating a sub-tenant',
        path: ['parentTenantId'],
      });
    }

    if (!data.createAcademySubtenant) {
      return;
    }

    if (data.tenantHierarchy !== 'root') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Academy subtenant can only be created with a root tenant',
        path: ['createAcademySubtenant'],
      });
    }

    if (data.isCustomer) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Academy subtenant can only be created for POV tenants',
        path: ['createAcademySubtenant'],
      });
    }

    if (data.povStatus !== 'portal-pov') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Academy subtenant can only be created with a TORCH POV (portal-pov) root',
        path: ['createAcademySubtenant'],
      });
    }
  });

export const CreateTenantResponseSchema = z.object({
  tenantId: z.string(),
  name: z.string(),
  adminAssignment: TenantAdminAssignmentResultSchema.optional(),
  academyTenant: z
    .object({
      tenantId: z.string(),
      name: z.string(),
    })
    .optional(),
});

export const UpdateTenantConfigSchema = z
  .object({
    isCustomer: z.boolean().optional(),
    povStatus: z.enum(TENANT_POV_STATUSES).nullable().optional(),
    priority: z.string().optional(),
    frequency: z.string().optional(),
    researcherEmail: z.email().nullish(),
    salesOwnerEmail: z.email().nullish(),
    csOwnerEmail: z.email().nullish(),
    expectedApplicationCount: z.int().min(0).nullish(),
  })
  .superRefine((data, ctx) => {
    if (data.isCustomer === true && data.povStatus != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Customer tenants cannot have a POV status',
        path: ['povStatus'],
      });
    }
  });

export type UpdateTenantConfigBody = z.infer<typeof UpdateTenantConfigSchema>;

export const ProvisionCompanyBodySchema = z.object({
  domain: CompanyDomainSchema,
});

export const ProvisionCompanyResponseSchema = z.object({
  accepted: z.boolean(),
  alreadyExists: z.boolean().optional(),
});

export const DeleteTenantDataQuerySchema = z.object({
  applicationId: z.string().min(1).optional(),
});

export const DeleteTenantDataResponseSchema = z.object({
  accepted: z.boolean(),
});

export const TENANT_POV_STATUS_FILTER_VALUES = [
  'all',
  ...TENANT_POV_STATUSES,
] as const;

export type TenantPovStatusFilter =
  (typeof TENANT_POV_STATUS_FILTER_VALUES)[number];

export const FINDING_PUBLISHED_FILTER_VALUES = [
  'all',
  'published',
  'unpublished',
] as const;

export type FindingPublishedFilter =
  (typeof FINDING_PUBLISHED_FILTER_VALUES)[number];

export const TenantMetricsFilterQuerySchema = z.object({
  createdStartDate: z.iso.datetime().optional(),
  createdEndDate: z.iso.datetime().optional(),
  signalStage: z.array(SignalStageSchema).optional(),
  signalStatus: z.array(SignalStatusSchema).optional(),
  findingStatus: z.array(FindingStatusSchema).optional(),
  findingPublished: z
    .enum(FINDING_PUBLISHED_FILTER_VALUES)
    .optional()
    .default('all'),
});

export const TENANT_CUSTOMER_FILTER_VALUES = [
  'all',
  'customer',
  'non-paying',
  'pov',
] as const;

export type TenantCustomerFilter =
  (typeof TENANT_CUSTOMER_FILTER_VALUES)[number];

export const TenantFilterQuerySchema = z
  .object({
    tenantId: z.string().optional(),
    search: z.string().max(200).optional(),
    isCustomer: z.enum(TENANT_CUSTOMER_FILTER_VALUES).optional().default('all'),
    povStatus: z
      .enum(TENANT_POV_STATUS_FILTER_VALUES)
      .optional()
      .default('all'),
    priority: z.string().optional(),
    frequency: z.string().optional(),
    researcherEmail: z.email().optional(),
    salesOwnerEmail: z.email().optional(),
    csOwnerEmail: z.email().optional(),
    hideTenantsTableArchived: z.stringbool().optional().default(false),
    limit: z.coerce.number().min(1).max(500).optional().prefault(50),
    offset: z.coerce.number().min(0).max(50_000).optional().prefault(0),
  })
  .merge(TenantMetricsFilterQuerySchema);

export const AppsOverviewQuerySchema = z.object({
  search: z.string().max(200).optional(),
  tenantId: z.string().optional(),
  isCustomer: z.enum(TENANT_CUSTOMER_FILTER_VALUES).optional().default('all'),
  priority: z.string().optional(),
  frequency: z.string().optional(),
  limit: z.coerce.number().min(1).max(500).optional().prefault(50),
  offset: z.coerce.number().min(0).max(50_000).optional().prefault(0),
});

export const AppOverviewItemSchema = AppHealthStatusSchema.extend({
  tenantId: z.string(),
  tenantName: z.string().nullish(),
});

export const AppsOverviewSchema = z.object({
  apps: z.array(AppOverviewItemSchema),
  total: z.number(),
});

export const FeatureFlagItemSchema = z.object({
  flagKey: z.string(),
  defaultValue: z.union([z.boolean(), z.number(), z.string(), z.null()]),
  type: z.string(),
  overrideCount: z.number(),
  supportedTargetTypes: z.array(
    z.enum(['tenant', 'application', 'user', 'segment']),
  ),
});

export const FeatureFlagRuleItemSchema = z.object({
  id: z.string(),
  flagKey: z.string(),
  rank: z.number(),
  targetType: z.enum(['tenant', 'application', 'user', 'segment']),
  targetId: z.string(),
  targetName: z.string().optional(),
  value: z.union([z.boolean(), z.number(), z.string(), z.null()]),
});

export const FeatureFlagDetailSchema = z.object({
  flagKey: z.string(),
  defaultValue: z.union([z.boolean(), z.number(), z.string(), z.null()]),
  type: z.string(),
  rules: z.array(FeatureFlagRuleItemSchema),
  supportedTargetTypes: z.array(
    z.enum(['tenant', 'application', 'user', 'segment']),
  ),
});

const FeatureFlagValueSchema = z.union([
  z.boolean(),
  z.number(),
  z.string(),
  z.null(),
]);

export const UpdateFeatureFlagRuleSchema = z.object({
  id: z.string().optional(),
  rank: z.number().optional(),
  targetType: z.enum(['tenant', 'application', 'user', 'segment']),
  targetId: z.string(),
  value: FeatureFlagValueSchema,
});

export const UpdateFeatureFlagBodySchema = z.object({
  defaultValue: FeatureFlagValueSchema.optional(),
  rules: z.array(UpdateFeatureFlagRuleSchema).optional(),
});

export const ServiceHealthItemSchema = z.object({
  name: z.string(),
  isHealthy: z.boolean(),
  port: z.number(),
  responseTimeMs: z.number().nullable(),
});

export const ScheduledJobSchema = z.object({
  schedulerId: z.string(),
  name: z.string(),
  service: z.string(),
  schedule: z.string(),
  description: z.string(),
});

// ============================================================================
// Kill Switch
// ============================================================================

export const KillSwitchStatusSchema = z.object({
  enabled: z.boolean(),
  enabledAt: z.iso.datetime().nullable(),
  enabledBy: z.string().nullable(),
  reason: z.string().nullable(),
});

export const KillSwitchToggleSchema = z.object({
  reason: z.string().max(500).optional(),
});

export const SchedulerKillSwitchScopeSchema = z.enum([
  'global',
  'scheduler',
  'tenant',
  'application',
]);

export const SchedulerKillSwitchToggleSchema = z.object({
  scope: SchedulerKillSwitchScopeSchema,
  schedulerId: z.string().min(1).optional(),
  tenantId: z.string().min(1).optional(),
  applicationId: z.string().min(1).optional(),
  reason: z.string().max(500).optional(),
});

export const SchedulerKillSwitchJobStatusSchema = z.object({
  schedulerId: z.string(),
  name: z.string(),
  description: z.string(),
  service: z.string(),
  schedule: z.string(),
  state: KillSwitchStatusSchema,
});

export const SchedulerKillSwitchOverrideSchema = z.object({
  scope: z.enum(['tenant', 'application']),
  tenantId: z.string().optional(),
  tenantName: z.string().optional(),
  applicationId: z.string().optional(),
  applicationName: z.string().optional(),
  schedulerId: z.string().nullable(),
  state: KillSwitchStatusSchema,
});

export const SchedulerKillSwitchSnapshotSchema = z.object({
  global: KillSwitchStatusSchema,
  schedulers: z.array(SchedulerKillSwitchJobStatusSchema),
  overrides: z.array(SchedulerKillSwitchOverrideSchema),
});

// ============================================================================
// Cost Management
// ============================================================================

export const CostSummarySchema = z.object({
  totalCostUsd: z.number(),
  totalInvocations: z.number(),
  totalTokens: z.number(),
  totalInputTokens: z.number(),
  totalOutputTokens: z.number(),
  totalReasoningTokens: z.number(),
  totalCacheReadTokens: z.number(),
  totalCacheWriteTokens: z.number(),
  avgCostPerInvocation: z.number(),
});

export const CostBreakdownItemSchema = z.object({
  key: z.string(),
  label: z.string(),
  costUsd: z.number(),
  invocations: z.number(),
  totalTokens: z.number(),
  reasoningTokens: z.number(),
  cacheReadTokens: z.number(),
  cacheWriteTokens: z.number(),
  percentage: z.number(),
  services: z.array(z.string()),
});

export const CostTrendPointSchema = z.object({
  date: z.string(),
  costUsd: z.number(),
  invocations: z.number(),
  tokens: z.number(),
  reasoningTokens: z.number(),
  cacheReadTokens: z.number(),
  cacheWriteTokens: z.number(),
});

export const CostGroupBySchema = z.enum([
  'tenant',
  'application',
  'model',
  'service',
  'function',
  'agent',
  'node',
  'nodePath',
  'rootOperation',
  'day',
]);
export type CostGroupBy = z.infer<typeof CostGroupBySchema>;

export const CostQuerySchema = z.object({
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  tenantId: z.string().optional(),
  applicationId: z.string().optional(),
  flowId: z.string().optional(),
  rootOperation: z.string().optional(),
  endpointId: z.string().optional(),
  signalId: z.string().optional(),
  serviceName: z.string().optional(),
  model: z.string().optional(),
  environment: z.enum(['development', 'staging', 'production']).optional(),
  groupBy: CostGroupBySchema.optional(),
  granularity: z.enum(['hour', 'day']).optional(),
});

export const CostByThreadQuerySchema = CostQuerySchema.extend({
  limit: z.coerce.number().min(1).max(100).optional().prefault(50),
  offset: z.coerce.number().min(0).max(50_000).optional().prefault(0),
  sortBy: z
    .enum(['costUsd', 'tokens', 'invocations', 'lastAt'])
    .optional()
    .default('lastAt'),
  sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
});

export const CostLicenseTypeTenantSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string(),
  costUsd: z.number(),
});

export const CostLicenseTypeOverviewSchema = z.object({
  customerSummary: CostSummarySchema,
  povSummary: CostSummarySchema,
  internalSummary: CostSummarySchema,
  unsetSummary: CostSummarySchema,
  customerTenants: z.array(CostLicenseTypeTenantSchema),
  customerTenantRemainingCount: z.number().int().nonnegative(),
  povTenants: z.array(CostLicenseTypeTenantSchema),
  povTenantRemainingCount: z.number().int().nonnegative(),
  internalTenants: z.array(CostLicenseTypeTenantSchema),
  internalTenantRemainingCount: z.number().int().nonnegative(),
  unsetTenants: z.array(CostLicenseTypeTenantSchema),
  unsetTenantRemainingCount: z.number().int().nonnegative(),
});

export const CostOverviewSchema = z.object({
  summary: CostSummarySchema,
  evalsSummary: CostSummarySchema,
  byTenant: z.array(CostBreakdownItemSchema),
  byModel: z.array(CostBreakdownItemSchema),
  byService: z.array(CostBreakdownItemSchema),
  trend: z.array(CostTrendPointSchema),
});

export const CostFlowSankeyExternalAssetBreakdownSchema = z.object({
  domains: z.int().nonnegative(),
  hosts: z.int().nonnegative(),
  services: z.int().nonnegative(),
});

export const CostFlowSankeyNodeSchema = z.object({
  id: z.string(),
  name: z.string(),
  column: z.enum([
    'surface',
    'category',
    'rootOperation',
    'step1',
    'step2',
    'step3',
    'step4',
    'total',
  ]),
  costUsd: z.number(),
  stepIndex: z.int().min(1).max(4).optional(),
  stepOperation: z.string().optional(),
  invocationCount: z.number().optional(),
  entityCount: z.number().optional(),
  externalAssetBreakdown: CostFlowSankeyExternalAssetBreakdownSchema.optional(),
  unattributedStepCostUsd: z.number().optional(),
});

export const CostFlowSankeyLinkSchema = z.object({
  from: z.string(),
  to: z.string(),
  costUsd: z.number(),
});

export const CostFlowSankeySchema = z.object({
  nodes: z.array(CostFlowSankeyNodeSchema),
  links: z.array(CostFlowSankeyLinkSchema),
  totalCostUsd: z.number(),
  totalAssets: z.number(),
});

export const CostThreadSummarySchema = z.object({
  threadId: z.string(),
  tenantId: z.string(),
  tenantName: z.string().optional(),
  applicationId: z.string().optional(),
  applicationName: z.string().optional(),
  totalCostUsd: z.number(),
  totalTokens: z.number(),
  reasoningTokens: z.number(),
  cacheReadTokens: z.number(),
  cacheWriteTokens: z.number(),
  invocations: z.number(),
  models: z.array(z.string()),
  firstAt: z.iso.datetime(),
  lastAt: z.iso.datetime(),
});

export const CostApplicationCategoryRowSchema = z.object({
  applicationId: z.string().optional(),
  applicationName: z.string(),
  tenantId: z.string(),
  tenantName: z.string().optional(),
  createdAt: z.iso.datetime().nullable(),
  targetType: FpTargetTypeSchema.optional(),
  totalAssets: z.number(),
  executionCount: z.number(),
  totalCostUsd: z.number(),
  discoveryCostUsd: z.number(),
  signalsCostUsd: z.number(),
  executionsCostUsd: z.number(),
  copilotCostUsd: z.number(),
  otherCostUsd: z.number(),
});

export const CostByApplicationCategorySchema = z.object({
  applications: z.array(CostApplicationCategoryRowSchema),
  totalApplications: z.number(),
  limit: z.number(),
});

// ============================================================================
// Platform Analytics
// ============================================================================

export const AnalyticsBreakdownItemSchema = z.object({
  key: z.string(),
  label: z.string(),
  count: z.number(),
  percentage: z.number(),
});

export const AnalyticsTenantItemSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string().optional(),
  count: z.number(),
  percentage: z.number(),
});

export const FindingsPublishedFilterSchema = z.enum([
  'all',
  'published',
  'unpublished',
]);

export const FindingAnalyticsTenantItemSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string().optional(),
  count: z.number(),
  retestPending: z.number(),
  retestCompleted: z.number(),
});

export const StatusTrendPointSchema = z.object({
  date: z.string(),
  total: z.number(),
  breakdown: z.array(z.object({ key: z.string(), count: z.number() })),
});

export const CategorySeverityPointSchema = z.object({
  category: z.string(),
  label: z.string(),
  total: z.number(),
  breakdown: z.array(z.object({ key: z.string(), count: z.number() })),
});

export const SignalStageStatusMatrixCellSchema = z.object({
  stage: z.string(),
  status: z.string(),
  count: z.number(),
});

export const AnalyticsQuerySchema = z.object({
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  tenantId: z.string().optional(),
});

export const FindingsAnalyticsQuerySchema = AnalyticsQuerySchema.extend({
  publishedFilter:
    FindingsPublishedFilterSchema.optional().default('published'),
});

export const FindingsTrendsQuerySchema = DateRangeQuerySchema.extend({
  publishedFilter:
    FindingsPublishedFilterSchema.optional().default('published'),
});

export const PostAttackAccuracySchema = z.object({
  totalTraces: z.number(),
  classifiedVulnerability: z.number(),
  classifiedSafe: z.number(),
  classifiedBlocked: z.number(),
  falsePositives: z.number(),
  falseNegatives: z.number(),
  fpRate: z.number(),
  fnRate: z.number(),
  matrix: z.array(
    z.object({
      attackLabel: z.string(),
      signalStage: z.string().optional(),
      signalStatus: z.string(),
      count: z.number(),
    }),
  ),
});

export const AutoFpAttackLabelSchema = z.enum([
  'CONFIRMED_VULNERABILITY',
  'SUSPECTED_VULNERABILITY',
]);

export const AutoFpHasTestCaseFilterSchema = z.enum(['any', 'with', 'without']);

export const OptionalCommaSeparatedTenantIdsSchema = z
  .string()
  .optional()
  .transform((value) =>
    value
      ? [
          ...new Set(
            value
              .split(',')
              .map((entry) => entry.trim())
              .filter((entry) => entry.length > 0),
          ),
        ]
      : undefined,
  )
  .pipe(z.array(z.string().min(1)).min(1).optional());

export const OptionalCommaSeparatedSignalCategoriesSchema = z
  .string()
  .optional()
  .transform((value) =>
    value
      ? value
          .split(',')
          .map((entry) => entry.trim())
          .filter((entry) => entry.length > 0)
      : undefined,
  )
  .pipe(z.array(SignalCategorySchema).min(1).optional());

export const AutoPublishedFindingOutcomesQuerySchema = z.object({
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  tenantId: z.string().optional(),
  tenantIds: OptionalCommaSeparatedTenantIdsSchema,
  targetType: FpTargetTypeSchema.optional(),
});

export const AutoPublishedFindingOutcomeSchema = z.object({
  key: z.string(),
  total: z.number().int().nonnegative(),
  falsePositives: z.number().int().nonnegative(),
  truePositives: z.number().int().nonnegative(),
});

export const AutoPublishedFindingOutcomesSchema = z.object({
  items: z.array(AutoPublishedFindingOutcomeSchema),
});

export type AutoPublishedFindingOutcomesQuery = z.infer<
  typeof AutoPublishedFindingOutcomesQuerySchema
>;
export type AutoPublishedFindingOutcome = z.infer<
  typeof AutoPublishedFindingOutcomeSchema
>;
export type AutoPublishedFindingOutcomes = z.infer<
  typeof AutoPublishedFindingOutcomesSchema
>;

export const AutoFpRateQuerySchema = z.object({
  attackLabel: AutoFpAttackLabelSchema,
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  tenantId: z.string().optional(),
  tenantIds: OptionalCommaSeparatedTenantIdsSchema,
  categories: OptionalCommaSeparatedSignalCategoriesSchema,
  hasTestCase: AutoFpHasTestCaseFilterSchema.optional().default('any'),
});

export const AutoFpRateTrendPointSchema = z.object({
  date: z.string(),
  falsePositives: z.number(),
  truePositives: z.number(),
  fpRate: z.number(),
});

export const AutoFpRateSchema = z.object({
  attackLabel: AutoFpAttackLabelSchema,
  totalCandidates: z.number(),
  falsePositives: z.number(),
  truePositives: z.number(),
  fpRate: z.number(),
  trend: z.array(AutoFpRateTrendPointSchema),
});

export const MatchedRuleAnalyticsQuerySchema = z.object({
  attackLabel: AutoFpAttackLabelSchema,
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  tenantId: z.string().optional(),
  tenantIds: OptionalCommaSeparatedTenantIdsSchema,
  categories: OptionalCommaSeparatedSignalCategoriesSchema,
});

export const MatchedRuleAnalyticsRowSchema = z.object({
  ruleId: z.string(),
  total: z.number(),
  truePositives: z.number(),
  falsePositives: z.number(),
  fpRate: z.number().nullable(),
  isLatestVersion: z.boolean(),
});

export const MatchedRuleAnalyticsSchema = z.object({
  attackLabel: AutoFpAttackLabelSchema,
  totalSignals: z.number(),
  rows: z.array(MatchedRuleAnalyticsRowSchema),
});

export const AutoFpSignalVerdictSchema = z.enum([
  'false_positive',
  'true_positive',
  'pending',
]);

export const AutoFpRateSignalsExportVerdictSchema = z.enum([
  'true_positive',
  'false_positive',
]);

export const AutoFpRateSignalsQuerySchema = AutoFpRateQuerySchema.extend({
  verdict: AutoFpRateSignalsExportVerdictSchema,
  fullExport: z.coerce.boolean().optional().prefault(false),
}).extend(
  PaginationQueryParamsSchema.extend({
    skip: z.coerce.number().int().min(0).optional().prefault(0),
    limit: z
      .int()
      .min(1)
      .max(PaginationDefaults.MAX_LIMIT)
      .optional()
      .default(PaginationDefaults.LIMIT),
  }).shape,
);

export const AutoFpSignalRowSchema = z.object({
  signalId: z.string(),
  tenantId: z.string(),
  applicationId: z.string(),
  category: z.string(),
  signalStage: SignalStageSchema,
  signalStatus: z.string(),
  attackLabel: z.string(),
  verdict: AutoFpSignalVerdictSchema,
  traceCreatedAt: z.string(),
  threadId: z.string(),
  hasPublishedFinding: z.boolean(),
  targetType: z.string(),
  targetId: z.string(),
  securityTestCaseId: z.string(),
  feedback: z.string(),
});

export const AutoFpSignalRowsPageSchema = z.object({
  items: z.array(AutoFpSignalRowSchema),
  total: z.int().min(0),
  skip: z.int().min(0),
  limit: z.int().min(1),
});

export const SignalPrecisionQuerySchema = z.object({
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  tenantId: z.string().optional(),
  tenantIds: OptionalCommaSeparatedTenantIdsSchema,
  categories: OptionalCommaSeparatedSignalCategoriesSchema,
  targetType: FpTargetTypeSchema.optional(),
});

export const SignalPrecisionSignalsQuerySchema =
  SignalPrecisionQuerySchema.extend({
    verdict: AutoFpRateSignalsExportVerdictSchema,
    fullExport: z.coerce.boolean().optional().prefault(false),
  }).extend(
    PaginationQueryParamsSchema.extend({
      skip: z.coerce.number().int().min(0).optional().prefault(0),
      limit: z
        .int()
        .min(1)
        .max(PaginationDefaults.MAX_LIMIT)
        .optional()
        .default(PaginationDefaults.LIMIT),
    }).shape,
  );

export const SignalPrecisionVerdictCountsSchema = z.object({
  truePositives: z.number(),
  falsePositives: z.number(),
  pending: z.number(),
  decisive: z.number(),
  precision: z.number().nullable(),
  statusConfirmed: z.number(),
  statusDisproven: z.number(),
  statusResolvedDecisive: z.number(),
});

export const SignalPrecisionBreakdownItemSchema =
  SignalPrecisionVerdictCountsSchema.extend({
    key: z.string(),
    label: z.string(),
  });

export const SignalPrecisionCohortPointSchema =
  SignalPrecisionVerdictCountsSchema.extend({
    period: z.string(),
  });

export const SignalPrecisionTimelinePointSchema = z.object({
  date: z.string(),
  category: z.string(),
  truePositives: z.number(),
  falsePositives: z.number(),
});

export const SignalPrecisionCohortDistributionSchema = z.object({
  period: z.string(),
  byTenant: z.array(SignalPrecisionBreakdownItemSchema),
  byCategory: z.array(SignalPrecisionBreakdownItemSchema),
  byConfidence: z.array(SignalPrecisionBreakdownItemSchema),
});

export const SignalPrecisionSchema = z.object({
  totals: SignalPrecisionVerdictCountsSchema.extend({
    totalSignals: z.number(),
  }),
  byTenant: z.array(SignalPrecisionBreakdownItemSchema),
  byCategory: z.array(SignalPrecisionBreakdownItemSchema),
  byConfidence: z.array(SignalPrecisionBreakdownItemSchema),
  cohorts: z.object({
    weekly: z.array(SignalPrecisionCohortPointSchema),
    monthly: z.array(SignalPrecisionCohortPointSchema),
  }),
  cohortDistributions: z.array(SignalPrecisionCohortDistributionSchema),
  timeline: z.array(SignalPrecisionTimelinePointSchema),
});

export type SignalPrecisionQuery = z.infer<typeof SignalPrecisionQuerySchema>;
export type SignalPrecisionVerdictCounts = z.infer<
  typeof SignalPrecisionVerdictCountsSchema
>;
export type SignalPrecisionBreakdownItem = z.infer<
  typeof SignalPrecisionBreakdownItemSchema
>;
export type SignalPrecisionCohortPoint = z.infer<
  typeof SignalPrecisionCohortPointSchema
>;
export type SignalPrecisionTimelinePoint = z.infer<
  typeof SignalPrecisionTimelinePointSchema
>;
export type SignalPrecisionCohortDistribution = z.infer<
  typeof SignalPrecisionCohortDistributionSchema
>;
export type SignalPrecision = z.infer<typeof SignalPrecisionSchema>;

export const SignalAnalyticsSchema = z.object({
  total: z.number(),
  byStage: z.array(AnalyticsBreakdownItemSchema),
  byStatus: z.array(AnalyticsBreakdownItemSchema),
  byBlockedReason: z.array(AnalyticsBreakdownItemSchema),
  byCategory: z.array(AnalyticsBreakdownItemSchema),
  byOrigin: z.array(AnalyticsBreakdownItemSchema),
  trend: z.array(StatusTrendPointSchema),
  trendByStatus: z.array(StatusTrendPointSchema),
  stageStatusMatrix: z.array(SignalStageStatusMatrixCellSchema),
  topTenants: z.array(AnalyticsTenantItemSchema),
  newLast7d: z.number(),
  newLast30d: z.number(),
  confirmedVulnerabilityRate: z.number(),
  postAttackAccuracy: PostAttackAccuracySchema.optional(),
});

export const FindingAnalyticsSchema = z.object({
  total: z.number(),
  bySeverity: z.array(AnalyticsBreakdownItemSchema),
  byStatus: z.array(AnalyticsBreakdownItemSchema),
  byCategory: z.array(AnalyticsBreakdownItemSchema),
  severityByCategory: z.array(CategorySeverityPointSchema),
  trend: z.array(StatusTrendPointSchema),
  topTenants: z.array(FindingAnalyticsTenantItemSchema),
  newLast7d: z.number(),
  newLast30d: z.number(),
  resolutionRate: z.number(),
  retestPending: z.number(),
  retestCompleted: z.number(),
});

export const ExecutionAnalyticsSchema = z.object({
  total: z.number(),
  byStatus: z.array(AnalyticsBreakdownItemSchema),
  byAgent: z.array(AnalyticsBreakdownItemSchema),
  trend: z.array(StatusTrendPointSchema),
  topTenants: z.array(AnalyticsTenantItemSchema),
  avgDurationMs: z.number(),
  medianDurationMs: z.number(),
  successRate: z.number(),
  newLast7d: z.number(),
  newLast30d: z.number(),
});

// ============================================================================
// Git Overview
// ============================================================================

export const GitMetricsSummarySchema = z.object({
  scannedPullRequests: z.number(),
  filesIndexed: z.number(),
  entryPointsFound: z.number(),
  relevantFilesFound: z.number(),
});

export const GitPrSummarySchema = z.object({
  totalPrs: z.number(),
  totalEntryPoints: z.number(),
  investigatedEntryPoints: z.number(),
  investigationRate: z.number(),
});

export const GitRepoItemSchema = z.object({
  name: z.string(),
  targetBranch: z.string().nullish(),
  applicationId: z.string(),
  applicationName: z.string().nullish(),
});

export const GitIntegrationItemSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  tenantName: z.string().nullish(),
  platform: z.string(),
  isDisabled: z.boolean().nullish(),
  gitHubAppKind: z.string().nullish(),
  baseUrl: z.string().nullish(),
  applicationCount: z.number(),
  repositoryCount: z.number(),
  repositories: z.array(GitRepoItemSchema),
  webhookEnabledCount: z.number(),
  createdAt: z.iso.datetime(),
});

export const GitTenantSummaryItemSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string().nullish(),
  integrationCount: z.number(),
  repositoryCount: z.number(),
  scannedPrs: z.number(),
  entryPoints: z.number(),
});

export const GitOverviewSchema = z.object({
  totalIntegrations: z.number(),
  totalApplicationIntegrations: z.number(),
  totalRepositories: z.number(),
  webhookEnabledRate: z.number(),
  metrics: GitMetricsSummarySchema,
  prs: GitPrSummarySchema,
  byPlatform: z.array(AnalyticsBreakdownItemSchema),
  topTenants: z.array(GitTenantSummaryItemSchema),
  integrations: z.array(GitIntegrationItemSchema),
});

export const GitQuerySchema = z.object({
  tenantId: z.string().optional(),
  platform: z.string().optional(),
  limit: z.coerce.number().min(1).max(500).optional().prefault(100),
  offset: z.coerce.number().min(0).max(50_000).optional().prefault(0),
});

export const TechnologyUsageQuerySchema = z.object({
  tenantId: z.string().optional(),
  tenantIds: OptionalCommaSeparatedTenantIdsSchema,
  limit: z.coerce.number().min(1).max(100).optional().prefault(20),
  offset: z.coerce.number().min(0).max(50_000).optional().prefault(0),
});

export const TechnologyUsageVersionSchema = z.object({
  version: z.string(),
  applicationCount: z.number(),
});

export const TechnologyUsageItemSchema = z.object({
  name: z.string(),
  aliases: z.array(z.string()),
  applicationCount: z.number(),
  versions: z.array(TechnologyUsageVersionSchema),
});

export const TechnologyUsageSchema = z.object({
  items: z.array(TechnologyUsageItemSchema),
  hasMore: z.boolean(),
});

export type TechnologyUsageQuery = z.infer<typeof TechnologyUsageQuerySchema>;
export type TechnologyUsage = z.infer<typeof TechnologyUsageSchema>;

// ============================================================================
// Crawler Overview
// ============================================================================

const CrawlerSessionStatusEnum = z.enum([
  'RUNNING',
  'ABORTING',
  'COMPLETED',
  'FAILED',
  'ABORTED',
]);

const CrawlerTypeEnum = z.enum([
  'SHALLOW',
  'AI',
  'MANUAL',
  'AUTHENTICATION',
  'FEATURE_ANALYSIS',
  'NAVIGATION_PROFILE',
]);

export const CrawlerSessionStatsSchema = z.object({
  harEntriesNew: z.number(),
  harEntriesExisting: z.number(),
  endpointsNew: z.number(),
  endpointsExisting: z.number(),
  pagesNew: z.number(),
  pagesExisting: z.number(),
});

export const CrawlerSessionItemSchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  tenantName: z.string().nullish(),
  applicationId: z.string(),
  applicationName: z.string().nullish(),
  crawlerType: CrawlerTypeEnum,
  status: CrawlerSessionStatusEnum,
  credentialId: z.string().nullable(),
  error: z.string().nullable(),
  createdAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  durationMs: z.number().nullable(),
  stats: CrawlerSessionStatsSchema.nullable(),
});

export const CrawlerOverviewSchema = z.object({
  totalSessions: z.number(),
  runningSessions: z.number(),
  completedSessions: z.number(),
  failedSessions: z.number(),
  byType: z.array(AnalyticsBreakdownItemSchema),
  sessions: z.array(CrawlerSessionItemSchema),
});

export const CrawlerQuerySchema = z.object({
  tenantId: z.string().optional(),
  applicationId: z.string().optional(),
  status: CrawlerSessionStatusEnum.optional(),
  crawlerType: CrawlerTypeEnum.optional(),
  limit: z.coerce.number().min(1).max(200).optional().prefault(50),
  offset: z.coerce.number().min(0).max(50_000).optional().prefault(0),
});

export const TriggerCrawlerRequestSchema = z.object({
  applicationId: z.string(),
  credentialId: z.string().nullable().optional(),
});

export const TriggerCrawlerResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

export const TriggerAuthenticationDiscoveryBodySchema = z.object({
  credentialId: z.string().min(1),
});

export const TriggerAuthenticationDiscoveryResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

export const TriggerFindingChainingReplayRequestSchema = z.object({
  applicationId: z.string(),
  limit: z.int().min(1).max(50).optional().default(5),
});

export const TriggerFindingChainingReplayResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  findingCount: z.number(),
  findingIds: z.array(z.string()),
});

// ============================================================================
// Endpoint Health & Code Graph Operations
// ============================================================================

export const EndpointHealthAppItemSchema = z.object({
  applicationId: z.string(),
  applicationName: z.string().nullish(),
  tenantId: z.string().nullable(),
  tenantName: z.string().nullish(),
  totalEndpoints: z.number(),
  withCodeGraph: z.number(),
  withoutCodeGraph: z.number(),
  graphCheckFailed: z.number(),
  outdated: z.number(),
  coveragePercent: z.number(),
});

export const EndpointHealthOverviewSchema = z.object({
  totalEndpoints: z.number(),
  withCodeGraph: z.number(),
  withoutCodeGraph: z.number(),
  graphCheckFailed: z.number(),
  outdated: z.number(),
  coveragePercent: z.number(),
  applications: z.array(EndpointHealthAppItemSchema),
});

export const EndpointHealthQuerySchema = z.object({
  tenantId: z.string().optional(),
  applicationId: z.string().optional(),
  limit: z.coerce.number().min(1).max(200).optional().prefault(50),
  offset: z.coerce.number().min(0).max(50_000).optional().prefault(0),
});

export const TriggerCodeGraphRequestSchema = z.object({
  endpointId: z.string(),
});

export const TriggerCodeGraphBulkRequestSchema = z.object({
  endpointIds: z.array(z.string()).min(1).max(200),
});

export const EndpointMissingCodeGraphQuerySchema = z.object({
  applicationId: z.string(),
  limit: z.coerce.number().min(1).max(500).optional().prefault(200),
  offset: z.coerce.number().min(0).max(50_000).optional().prefault(0),
});

export const EndpointMissingCodeGraphItemSchema = z.object({
  id: z.string(),
  path: z.string(),
  method: z.string().nullish(),
  graphCheckFailed: z.boolean().nullable(),
  graphCheckFailedReason: z.string().nullable(),
});

export const EndpointMissingCodeGraphListSchema = z.object({
  endpoints: z.array(EndpointMissingCodeGraphItemSchema),
  total: z.number(),
});

export const TriggerMissingCodeGraphsRequestSchema = z.object({
  tenantId: z.string().optional(),
  applicationId: z.string().optional(),
});

export const ResetFailedGraphsRequestSchema = z.object({
  applicationId: z.string(),
});

export const TriggerStupidSignalsRequestSchema = z.object({
  applicationId: z.string(),
  endpointIds: z.array(z.string()).min(1).max(200),
});

export const TriggerSmartSignalsRequestSchema = z.object({
  applicationId: z.string(),
  endpointIds: z.array(z.string()).min(1).max(200),
});

export const TriggerAiSignalGenerationRequestSchema = z.object({
  applicationId: z.string(),
});

export const TriggerDeepActiveProbeAllRequestSchema = z.object({
  applicationId: z.string(),
});

export const TriggerDeepActiveProbeAllResponseSchema = z.discriminatedUnion(
  'outcome',
  [
    z.object({
      outcome: z.literal('QUEUED'),
      queuedCount: z.number(),
      skipped: z.object({
        outOfTestingScope: z.number(),
        noHarEntry: z.number(),
      }),
    }),
    z.object({
      outcome: z.literal('BLOCKED'),
      reason: z.enum([
        'OFFENSIVE_OPERATIONS_PAUSED',
        'SIGNAL_CAPACITY_REACHED',
      ]),
    }),
  ],
);

export const DeepActiveProbePauseStatusSchema = z.object({
  paused: z.boolean(),
  pausedAt: z.string().nullable(),
  pausedBy: z.string().nullable(),
});

export const SetDeepActiveProbePauseRequestSchema = z.object({
  paused: z.boolean(),
});

export const EndpointActionResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

export const SmartSignalActionResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  triggeredCount: z.number(),
  skippedCount: z.number(),
});

export const EndpointBulkActionSchema = z.enum([
  'CODE_GRAPH',
  'STUPID_SIGNALS',
  'SECURITY_TEST_CASES',
]);

export const EndpointBulkRunItemStatusSchema = z.enum([
  'PENDING',
  'QUEUED',
  'FAILED',
  'SKIPPED',
  'BLOCKED',
]);

export const EndpointActionEligibilitySchema = z.object({
  eligible: z.boolean(),
  reason: z.string().nullable(),
});

export const EndpointWithCodeGraphStatusSchema = z.object({
  id: z.string(),
  path: z.string(),
  method: z.string().nullable(),
  hasCodeGraph: z.boolean(),
  graphCheckFailed: z.boolean(),
  graphCheckFailedReason: z.string().nullable(),
  hasHarEntry: z.boolean(),
  harAnalysisCompleted: z.boolean(),
  signalCount: z.number(),
  codeGraphEligibility: EndpointActionEligibilitySchema,
  stupidSignalsEligibility: EndpointActionEligibilitySchema,
  securityTestCasesEligibility: EndpointActionEligibilitySchema,
});

export const AllEndpointsQuerySchema = z.object({
  applicationId: z.string(),
  search: z.string().optional(),
  codeGraphFilter: z.enum(['all', 'with', 'without']).optional(),
  actionEligibility: EndpointBulkActionSchema.optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});

export const AllEndpointsResponseSchema = z.object({
  total: z.number(),
  allCount: z.number(),
  withCodeGraph: z.number(),
  withoutCodeGraph: z.number(),
  endpoints: z.array(EndpointWithCodeGraphStatusSchema),
});

export const EndpointBulkRunFilterSchema = z.object({
  search: z.string().optional(),
  codeGraphFilter: z.enum(['all', 'with', 'without']).optional(),
  actionEligibility: EndpointBulkActionSchema.optional(),
});

export const StartEndpointBulkRunRequestSchema = z
  .object({
    applicationId: z.string(),
    action: EndpointBulkActionSchema,
    endpointIds: z.array(z.string()).min(1).max(2000).optional(),
    allMatching: z.boolean().optional(),
    filter: EndpointBulkRunFilterSchema.optional(),
  })
  .refine(
    ({ endpointIds, allMatching }) =>
      (endpointIds !== undefined) !== (allMatching === true),
    {
      message: 'Provide endpointIds or allMatching, but not both',
    },
  );

export const EndpointBulkRunItemSchema = z.object({
  endpointId: z.string(),
  path: z.string(),
  method: z.string().nullable(),
  status: EndpointBulkRunItemStatusSchema,
  reason: z.string().nullable(),
  attempts: z.number().int().nonnegative(),
  updatedAt: z.iso.datetime(),
});

export const EndpointBulkRunSchema = z.object({
  id: z.string(),
  applicationId: z.string(),
  action: EndpointBulkActionSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  items: z.array(EndpointBulkRunItemSchema),
  counts: z.object({
    total: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    queued: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    blocked: z.number().int().nonnegative(),
  }),
});

export const EndpointBulkRunParamsSchema = z.object({
  runId: z.string(),
});

export const ActiveEndpointBulkRunQuerySchema = z.object({
  applicationId: z.string(),
  action: EndpointBulkActionSchema.optional(),
});

export const ActiveEndpointBulkRunResponseSchema = z.object({
  runs: z.array(EndpointBulkRunSchema),
});

export const DismissEndpointBulkRunResponseSchema = z.object({
  run: EndpointBulkRunSchema.nullable(),
});

export const EndpointSourceCoverageQuerySchema = z.object({
  tenantId: z.string().optional(),
  applicationId: z.string().optional(),
});

export const EndpointSourceCoverageItemSchema = z.object({
  source: EndpointSourceSchema,
  totalDiscovered: z.number(),
  uniqueDiscovered: z.number(),
});

export const EndpointSourceCoverageOverviewSchema = z.object({
  totalEndpoints: z.number(),
  bySource: z.array(EndpointSourceCoverageItemSchema),
});

export const CockpitAuditLogItemSchema = z.object({
  id: z.string(),
  userId: z.string().nullable(),
  actorName: z.string().nullable(),
  tenantId: z.string().nullable(),
  userRoles: z.array(z.string()),
  permissions: z.array(z.string()),
  action: z.string(),
  actionName: z.string(),
  method: z.string(),
  path: z.string(),
  routePath: z.string().nullable(),
  statusCode: z.number(),
  isSuccessful: z.boolean(),
  requestBody: z.unknown().nullable(),
  requestQuery: z.unknown().nullable(),
  requestParams: z.unknown().nullable(),
  metadata: z.unknown().nullable(),
  errorMessage: z.string().nullable(),
  createdAt: z.iso.datetime(),
});

export const CockpitAuditLogsQuerySchema = z.object({
  limit: z.coerce.number().min(1).max(200).optional().prefault(50),
  offset: z.coerce.number().min(0).max(50_000).optional().prefault(0),
  method: z.enum(['POST', 'PUT', 'PATCH', 'DELETE']).optional(),
  action: z.string().max(200).optional(),
  actionName: z.string().max(200).optional(),
  userId: z.string().optional(),
  actorName: z.string().max(320).optional(),
  isSuccessful: z.coerce.boolean().optional(),
  search: z.string().max(200).optional(),
});

export const CockpitAuditLogFiltersQuerySchema = z.object({
  isSuccessful: z.coerce.boolean().optional(),
});

export const CockpitAuditLogsResponseSchema = z.object({
  logs: z.array(CockpitAuditLogItemSchema),
  total: z.number(),
});

export const CockpitAuditLogFiltersResponseSchema = z.object({
  actions: z.array(z.string()),
  actors: z.array(z.string()),
});

export const CockpitUserItemSchema = z.object({
  userId: z.string(),
  email: z.string().nullable(),
  name: z.string().nullable(),
  roles: z.array(z.string()),
  permissions: z.array(z.string()),
});

export const CockpitUsersResponseSchema = z.object({
  users: z.array(CockpitUserItemSchema),
  total: z.number(),
});

export const K8sOperatorStatusSchema = z.enum([
  'UNINITIALIZED',
  'CONNECTED',
  'DISCONNECTED',
]);

export const K8sOperatorItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: K8sOperatorStatusSchema,
  createdAt: z.iso.datetime(),
  createdBy: z.string().nullable(),
  accessKeyMasked: z.string(),
  commandsQueueUrl: z.string().nullable(),
  bridgeRoleArn: z.string().nullable(),
  externalId: z.string().nullable(),
  sqsRegion: z.string().nullable(),
  operatorVersion: z.string().nullable(),
  initializedAt: z.iso.datetime().nullable(),
  lastHeartbeatAt: z.iso.datetime().nullable(),
});

export const K8sOperatorsResponseSchema = z.object({
  operators: z.array(K8sOperatorItemSchema),
  total: z.number(),
});

export const CreateK8sOperatorBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
});

export const CreateK8sOperatorResponseSchema = z.object({
  operator: K8sOperatorItemSchema,
  accessKey: z.string(),
});

export const RotateK8sOperatorAccessKeyResponseSchema = z.object({
  operator: K8sOperatorItemSchema,
  accessKey: z.string(),
});

export const K8sOperatorDetailResponseSchema = z.object({
  operator: K8sOperatorItemSchema.extend({
    descopeAccessKeyId: z.string().nullable(),
    initializationCrdJson: z.string().nullable(),
  }),
});

export const DeleteK8sOperatorResponseSchema = z.object({
  success: z.boolean(),
});

export const PublishK8sOperatorMessageBodySchema = z.object({
  message: z
    .string()
    .trim()
    .min(1)
    .max(20000)
    .refine(
      (value) => {
        try {
          JSON.parse(value);

          return true;
        } catch {
          return false;
        }
      },
      {
        error: 'message must be valid JSON',
      },
    ),
});

export const PublishK8sOperatorMessageResponseSchema = z.object({
  success: z.boolean(),
  executionId: z.string(),
});

export const PublishK8sOperatorKubectlBodySchema = z.object({
  command: z.string().trim().min(1).max(10_000),
  timeout: z.string().trim().min(1).max(50).optional(),
  namespace: z.string().trim().min(1).max(120).optional(),
  kubecontext: z.string().trim().min(1).max(120).optional(),
  redactPatterns: z.array(z.string().trim().min(1).max(500)).max(50).optional(),
});

export const PublishK8sOperatorKubectlResponseSchema = z.object({
  success: z.boolean(),
  executionId: z.string(),
});

export const PublishK8sOperatorHelmCommandBodySchema = z.object({
  command: z.string().trim().min(1).max(10_000),
  timeout: z.string().trim().min(1).max(50).optional(),
});

export const PublishK8sOperatorHelmCommandResponseSchema = z.object({
  success: z.boolean(),
  executionId: z.string(),
});

export const PublishK8sOperatorHelmUpgradeOptionsSchema = z.object({
  noHooks: z.boolean().optional(),
  force: z.boolean().optional(),
  recreate: z.boolean().optional(),
  atomic: z.boolean().optional(),
  cleanupOnFail: z.boolean().optional(),
  wait: z.boolean().optional(),
  waitForJobs: z.boolean().optional(),
  dryRun: z.boolean().optional(),
  disableOpenapiValidation: z.boolean().optional(),
  skipSchemaValidation: z.boolean().optional(),
  skipCrds: z.boolean().optional(),
  takeOwnership: z.boolean().optional(),
  dependencyUpdate: z.boolean().optional(),
  timeout: z
    .string()
    .trim()
    .regex(/^\d+(ms|s|m|h)$/)
    .optional(),
});

export const PublishK8sOperatorHelmUpgradeBodySchema = z
  .object({
    name: z.string().trim().min(1).max(53),
    namespace: z.string().trim().min(1).max(63),
    valuesYaml: z.string().max(200_000),
    repo: z.string().trim().max(400).optional(),
    chart: z.string().trim().max(200).optional(),
    version: z.string().trim().max(200).optional(),
    options: PublishK8sOperatorHelmUpgradeOptionsSchema.optional(),
  })
  .refine(
    (value) => {
      const provided = [value.repo, value.chart, value.version].filter(
        (part) => (part?.trim().length ?? 0) > 0,
      );

      return provided.length === 0 || provided.length === 3;
    },
    {
      error: 'repo, chart, and version must all be set or all be omitted',
    },
  );

export const PublishK8sOperatorManifestActionSchema = z.enum([
  'create',
  'delete',
]);

export const PublishK8sOperatorManifestBodySchema = z.object({
  manifestYaml: z.string().trim().min(1).max(200_000),
  action: PublishK8sOperatorManifestActionSchema.optional(),
});

export const PublishK8sOperatorManifestResponseSchema = z.object({
  success: z.boolean(),
  executionId: z.string(),
  manifestUrl: z.url(),
});

export const K8sOperatorExecutionStatusSchema = z.enum([
  'pending',
  'in_progress',
  'successful',
  'failed',
]);

export const K8sOperatorExecutionItemSchema = z.object({
  id: z.string(),
  operatorId: z.string(),
  executionId: z.string(),
  taskType: z.string(),
  status: K8sOperatorExecutionStatusSchema,
  statusMessage: z.string().nullable(),
  output: z.string().nullable(),
  requestedBy: z.string().nullable(),
  payload: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const GetK8sOperatorExecutionsResponseSchema = z.object({
  executions: z.array(K8sOperatorExecutionItemSchema),
});

export const K8sOperatorHelmReleaseItemSchema = z.object({
  id: z.string(),
  operatorId: z.string(),
  releaseKey: z.string(),
  name: z.string(),
  namespace: z.string(),
  repo: z.string(),
  chart: z.string(),
  version: z.string().nullable(),
  values: z.record(z.string(), z.unknown()).nullable(),
  wait: z.boolean(),
  advanced: z.record(z.string(), z.unknown()),
  lastExecutionId: z.string().nullable(),
  status: K8sOperatorExecutionStatusSchema,
  statusMessage: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const GetK8sOperatorHelmReleasesResponseSchema = z.object({
  releases: z.array(K8sOperatorHelmReleaseItemSchema),
});

export const GetK8sOperatorHelmReleasesQuerySchema = z.object({
  namespace: z.string().trim().min(1).max(120).optional(),
});

export const UpdateK8sOperatorHelmReleaseBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  namespace: z.string().trim().min(1).max(120),
  repo: z.string().trim().min(1).max(400),
  chart: z.string().trim().min(1).max(200),
  version: z.string().trim().max(200).nullable().optional(),
  values: z.record(z.string(), z.unknown()).nullable().optional(),
  wait: z.boolean().optional(),
  advanced: z.record(z.string(), z.unknown()).optional(),
});

export const UpdateK8sOperatorHelmReleaseResponseSchema = z.object({
  success: z.boolean(),
  release: K8sOperatorHelmReleaseItemSchema,
});

export const PublishK8sOperatorHelmReleaseResponseSchema = z.object({
  success: z.boolean(),
  executionId: z.string(),
});

export const K8sOperatorActivityItemSchema = z.object({
  id: z.string(),
  operatorId: z.string(),
  type: z.string(),
  message: z.string(),
  source: z.enum(['user', 'operator', 'system']),
  actor: z.string().nullable(),
  httpMethod: z.string().nullable(),
  httpPath: z.string().nullable(),
  httpStatusCode: z.number().nullable(),
  metadata: z.unknown().nullable(),
  createdAt: z.iso.datetime(),
});

export const GetK8sOperatorActivityResponseSchema = z.object({
  activities: z.array(K8sOperatorActivityItemSchema),
});

export const InitializeK8sOperatorBodySchema = z.object({
  crd: z.record(z.string(), z.unknown()),
});

export const InitializeK8sOperatorResponseSchema = z.object({
  success: z.boolean(),
  commandsQueueUrl: z.string(),
});

export const GetK8sOperatorSqsCredentialsResponseSchema = z.object({
  commandsQueueUrl: z.string(),
  region: z.string(),
  accessKeyId: z.string(),
  secretAccessKey: z.string(),
  sessionToken: z.string(),
  expiresAt: z.string(),
});

export const HeartbeatK8sOperatorResponseSchema = z.object({
  success: z.boolean(),
  status: K8sOperatorStatusSchema,
  lastHeartbeatAt: z.iso.datetime(),
  version: z.string(),
});

export const HeartbeatK8sOperatorBodySchema = z.object({
  version: z.string().trim().min(1).max(120),
});

export const K8sOperatorInfoResponseSchema = z.object({
  operator: z.object({
    name: z.string(),
    status: K8sOperatorStatusSchema,
    commandsQueueUrl: z.string().nullable(),
    version: z.string().nullable(),
    lastHeartbeatAt: z.iso.datetime().nullable(),
  }),
});

export const UpdateK8sOperatorExecutionStatusBodySchema = z.object({
  executionId: z.string().trim().min(1).max(200),
  status: z.enum(['in_progress', 'successful', 'failed']),
  statusMessage: z.string().trim().max(1000).optional(),
});

export const UpdateK8sOperatorExecutionStatusResponseSchema = z.object({
  success: z.boolean(),
});

export const UpdateK8sOperatorExecutionOutputChunkBodySchema = z
  .object({
    executionId: z.string().trim().min(1).max(200).optional(),
    execution_id: z.string().trim().min(1).max(200).optional(),
    chunk: z.string().max(50_000).optional(),
    outputChunk: z.string().max(50_000).optional(),
    output_chunk: z.string().max(50_000).optional(),
    content: z.string().max(50_000).optional(),
    stream: z.enum(['stdout', 'stderr']).optional(),
    statusMessage: z.string().trim().max(1000).optional(),
    status_message: z.string().trim().max(1000).optional(),
  })
  .refine((value) => Boolean(value.executionId ?? value.execution_id), {
    error: 'executionId/execution_id is required',
  })
  .refine(
    (value) =>
      Boolean(
        value.chunk ?? value.outputChunk ?? value.output_chunk ?? value.content,
      ),
    {
      error: 'chunk/outputChunk/output_chunk/content is required',
    },
  );

export const UpdateK8sOperatorExecutionOutputChunkResponseSchema = z.object({
  success: z.boolean(),
});

export const UpdateK8sOperatorExecutionOutputFinalBodySchema = z
  .object({
    executionId: z.string().trim().min(1).max(200).optional(),
    execution_id: z.string().trim().min(1).max(200).optional(),
    output: z.string().max(500_000).optional(),
    finalOutput: z.string().max(500_000).optional(),
    final_output: z.string().max(500_000).optional(),
    content: z.string().max(500_000).optional(),
    statusMessage: z.string().trim().max(1000).optional(),
    status_message: z.string().trim().max(1000).optional(),
  })
  .refine((value) => Boolean(value.executionId ?? value.execution_id), {
    error: 'executionId/execution_id is required',
  })
  .refine(
    (value) =>
      Boolean(
        value.output ??
          value.finalOutput ??
          value.final_output ??
          value.content,
      ),
    {
      error: 'output/finalOutput/final_output/content is required',
    },
  );

export const UpdateK8sOperatorExecutionOutputFinalResponseSchema = z.object({
  success: z.boolean(),
});

export const RequestK8sOperatorHelmListBodySchema = z.object({
  namespace: z.string().trim().min(1).max(120).optional(),
});

export const RequestK8sOperatorHelmListResponseSchema = z.object({
  success: z.boolean(),
  executionId: z.string(),
});

export const ReportK8sOperatorHelmListItemSchema = z.object({
  name: z.string().trim().min(1).max(120),
  namespace: z.string().trim().min(1).max(120),
  chart: z.string().trim().min(1).max(200),
  repo: z.string().trim().max(400).nullable().optional(),
  version: z.string().trim().max(200).nullable().optional(),
  values: z.record(z.string(), z.unknown()).nullable().optional(),
});

export const ReportK8sOperatorHelmListBodySchema = z.object({
  executionId: z.string().trim().min(1).max(200),
  namespace: z.string().trim().min(1).max(120).optional(),
  releases: z.array(ReportK8sOperatorHelmListItemSchema),
});

export const ReportK8sOperatorHelmListResponseSchema = z.object({
  success: z.boolean(),
});

export const TraceMessageItemSchema = z.object({
  id: z.string(),
  role: z.enum(['human', 'ai', 'tool', 'system', 'function']),
  content: z.string().nullable(),
  reasoning: z.string().optional(),
  toolCalls: z
    .array(
      z.object({
        id: z.string().optional(),
        name: z.string(),
        args: z.string().optional(),
      }),
    )
    .optional(),
  toolCallId: z.string().optional(),
  toolName: z.string().optional(),
  agentName: z.string().optional(),
  createdAt: z.iso.datetime(),
});

export const TraceFileItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  size: z.number(),
  createdAt: z.iso.datetime(),
});

export const TraceDetailTraceSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: TraceStatusSchema,
  signalId: z.string().nullable(),
  metadata: z.unknown().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  durationMs: z.number().nullable(),
  totalCostUsd: z.number().nullable(),
  messages: z.array(TraceMessageItemSchema),
  files: z.array(TraceFileItemSchema),
});

export const TraceDetailSchema = z.object({
  threadId: z.string(),
  tenantId: z.string().nullable(),
  tenantName: z.string().nullish(),
  applicationId: z.string().nullable(),
  applicationName: z.string().nullish(),
  totalCostUsd: z.number().nullable(),
  traces: z.array(TraceDetailTraceSchema),
});

export {
  TriggerEvaluationRunRequestSchema,
  TriggerEvaluationRunResponseSchema,
  EvaluationRunStatusSchema,
  EvaluationRunKindSchema,
  EvaluationRunScenarioInfoSchema,
  EngineSummaryMetricsSchema,
  EngineSummaryIterationSchema,
  EvaluationRunListItemSchema,
  EvaluationRunDetailSchema,
  EvaluationRunLogsSchema,
  EvaluationRunArtifactsSchema,
  EvaluationRunArtifactContentSchema,
  RerunEvaluationRunRequestSchema,
  RerunEvaluationRunResponseSchema,
  UploadEvaluationScenarioRequestSchema,
  UploadEvaluationScenarioResponseSchema,
  EvaluationScenarioListItemSchema,
  ListEvaluationScenariosQuerySchema,
  ListEvaluationScenariosResponseSchema,
  EVALUATION_SIGNALS_LIST_MAX_LIMIT,
  ListEvaluationSignalsQuerySchema,
  EvaluationSignalListItemSchema,
  ListEvaluationSignalsResponseSchema,
  type EvaluationRunScenarioInfo,
  type EvaluationEngineSummaryMetrics,
  type EvaluationEngineSummaryIteration,
} from './evaluation-run.schemas';

export const AppCommentSchema = z.object({
  id: z.string(),
  content: z.string(),
  authorId: z.string(),
  createdAt: z.iso.datetime(),
});

export const AppNotesSchema = z.object({
  applicationId: z.string(),
  status: z.string(),
  description: z.string(),
  comments: z.array(AppCommentSchema),
});

export const UpdateAppStatusBodySchema = z.object({
  status: z.string(),
});

export const UpdateAppDescriptionBodySchema = z.object({
  description: z.string(),
});

export const UpdateAppResearcherBodySchema = z.object({
  researcherEmail: z.email().nullable(),
});

export const CreateAppCommentBodySchema = z.object({
  content: z.string().min(1),
});

export const AppsStatusesSchema = z.record(z.string(), z.string());

export const AppSubitemSchema = z.object({
  applicationId: z.string(),
  name: z.string(),
  createdAt: z.iso.datetime(),
  status: z.string(),
  description: z.string(),
  researcherEmail: z.email().nullable(),
  codeGraphLimit: z.number().int().nullable(),
  signalGenerationLimit: z.number().int().nullable(),
  codeGraphGenerated: z.number().int().nonnegative(),
});

export type AppSubitem = z.infer<typeof AppSubitemSchema>;

export const TenantAppSubitemsSchema = z.object({
  subitems: z.array(AppSubitemSchema),
});

export const CoPilotSkillSecurityDomainSchema = z.enum(
  COPILOT_SKILL_SECURITY_DOMAINS,
);

export const CoPilotSkillClientSurfaceSchema = z.enum(
  COPILOT_SKILL_CLIENT_SURFACES,
);

export const CoPilotSkillAvailabilitySchema = z.object({
  securityDomain: CoPilotSkillSecurityDomainSchema,
  sessionDomains: z.array(CoPilotSkillSecurityDomainSchema),
  clientSurfaces: z.array(CoPilotSkillClientSurfaceSchema),
  requiresProxyTools: z.boolean(),
  requiresTerraMcpTools: z.boolean(),
});

export const CoPilotSkillDetailSchema = z.object({
  skillId: z.string(),
  bundleId: z.string(),
  skillDir: z.string(),
  virtualPath: z.string(),
  name: z.string(),
  description: z.string(),
  bodyMarkdown: z.string(),
  mentionedToolIds: z.array(z.string()),
  availability: CoPilotSkillAvailabilitySchema,
});

export const CoPilotSkillAgentSchema = z.object({
  id: z.string(),
  role: z.enum(['main', 'subagent']),
  label: z.string(),
  summary: z.string(),
  bundleId: z.string(),
  skillsVirtualPath: z.string(),
  toolIds: z.array(z.string()),
  systemPrompt: z.string(),
  securityDomain: CoPilotSkillSecurityDomainSchema,
  sessionDomains: z.array(CoPilotSkillSecurityDomainSchema),
  skills: z.array(CoPilotSkillDetailSchema),
});

export const CoPilotSkillsCatalogSchema = z.object({
  agents: z.array(CoPilotSkillAgentSchema),
});

export const CoPilotCustomSkillsQuerySchema = z.object({
  tenantId: z.string().optional(),
  applicationId: z.string().optional(),
  agentId: z.string().optional(),
  status: SkillStatusSchema.optional(),
  search: z.string().max(200).optional(),
  skip: z.coerce.number().int().nonnegative().optional().prefault(0),
  limit: z.coerce.number().int().positive().max(200).optional().prefault(200),
});

export const CoPilotCustomSkillsResponseSchema = z.object({
  skills: z.array(
    SkillSummarySchema.extend({
      createdByUserId: z.string().nullable(),
      mentionedToolIds: z.array(z.string()),
      availability: CoPilotSkillAvailabilitySchema,
    }),
  ),
  total: z.int().nonnegative(),
});

export const CoPilotCustomSkillResponseSchema = GetSkillResponseSchema.extend({
  createdByUserId: z.string().nullable(),
  mentionedToolIds: z.array(z.string()),
  availability: CoPilotSkillAvailabilitySchema,
});

export const CopilotFilesystemFileSchema = z.object({
  path: z.string(),
  content: z.string(),
  createdAt: z.string(),
  modifiedAt: z.string(),
});

export const CopilotThreadFilesystemResponseSchema = z.object({
  tenantId: z.string(),
  threadId: z.string(),
  files: z.array(CopilotFilesystemFileSchema),
});

export const CopilotThreadFilesystemQuerySchema = z.object({
  path: z.string().optional(),
});

export const FpFailureCategorySchema = FeedbackFailureCategorySchema;

export type FpFailureCategory = z.infer<typeof FpFailureCategorySchema>;

export const FpFeedbackSourceSchema = z.enum(['signal', 'finding']);
export type FpFeedbackSource = z.infer<typeof FpFeedbackSourceSchema>;

export const FpStatusChangeSchema = z.enum([
  'CONFIRMED_TO_DISPROVEN',
  'POTENTIAL_TO_DISPROVEN',
]);

export type FpStatusChange = z.infer<typeof FpStatusChangeSchema>;

export const FpAnalyticsQuerySchema = z.object({
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  tenantId: z.string().optional(),
  tenantIds: OptionalCommaSeparatedTenantIdsSchema,
  targetType: FpTargetTypeSchema.optional(),
  source: FpFeedbackSourceSchema.optional().default('signal'),
  failureCategory: FpFailureCategorySchema.optional(),
  failureArea: FeedbackFailureAreaSchema.optional(),
  signalCategory: z.string().optional(),
  findingSeverity: z
    .union([FindingSeveritySchema, z.literal('__unknown__')])
    .optional(),
  statusChange: FpStatusChangeSchema.optional(),
  hasTesterFeedback: OptionalBooleanQueryParamSchema,
  hasTraceAnalysis: OptionalBooleanQueryParamSchema,
  traceAssisted: OptionalBooleanQueryParamSchema,
  submitterId: z.string().optional(),
});

export const FpFeedbackQuerySchema = FpAnalyticsQuerySchema.extend({
  search: z.string().max(200).optional(),
  page: z.coerce.number().int().min(1).optional().prefault(1),
  pageSize: z.coerce.number().int().min(1).max(100).optional().prefault(20),
  fullExport: BooleanQueryParamDefaultFalseSchema,
});

export const FpInvestigationChecksSchema = z.object({
  origin: SignalOriginSchema.optional().catch(undefined),
  sourceProvider: z.string().nullable().optional(),
  sourceLabel: z.string().nullable().optional(),
  codeConnected: z.boolean().optional(),
  businessContextScore: z
    .number()
    .int()
    .min(0)
    .max(100)
    .nullable()
    .optional()
    .catch(undefined),
});

export type FpInvestigationChecks = z.infer<typeof FpInvestigationChecksSchema>;

export const FpApplicationBehaviorChecksSchema = z.object({
  businessContextConfigured: z.boolean().optional(),
  businessContextToolRan: z.boolean().optional(),
});

export const FpApplicationBehaviorSchema = z.object({
  type: z.enum(['authorization', 'other']),
  confidence: z.number().min(0).max(1).optional(),
  rationale: z.string().optional(),
  checks: FpApplicationBehaviorChecksSchema.optional(),
  outcome: z
    .enum(['memory', 'investigation', 'notification', 'none'])
    .optional(),
});

export type FpApplicationBehavior = z.infer<typeof FpApplicationBehaviorSchema>;

export const FpEndpointMemoryRecordSchema = z.object({
  recordId: z.string(),
  content: z.string(),
  createdAt: z.string().optional(),
  changeType: z.enum(['added', 'updated']).optional(),
});

export type FpEndpointMemoryRecord = z.infer<
  typeof FpEndpointMemoryRecordSchema
>;

export const FpFeedbackRowSchema = z.object({
  s3Key: z.string(),
  source: FpFeedbackSourceSchema,
  signalId: z.string().optional(),
  findingId: z.string().optional(),
  applicationId: z.string(),
  tenantId: z.string(),
  tenantName: z.string().optional(),
  targetType: FpTargetTypeSchema.optional(),
  userId: z.string(),
  userDisplayName: z.string().optional(),
  threadId: z.string().optional(),
  previousStage: SignalStageSchema.nullable().optional(),
  previousStatus: SignalStatusSchema.nullable().optional(),
  newStage: SignalStageSchema.nullable().optional(),
  newStatus: SignalStatusSchema.nullable().optional(),
  previousFindingStatus: z.string().optional(),
  newFindingStatus: z.string().optional(),
  findingTitle: z.string().optional(),
  findingCategory: z.string().optional(),
  findingSeverity: z.string().optional(),
  failureCategory: z.string().optional(),
  failureArea: FeedbackFailureAreaSchema.optional(),
  categoryClassificationPass: CategoryClassificationPassSchema.optional(),
  categoryConfidence: z.number().min(0).max(1).optional(),
  categoryRationale: z.string().optional(),
  feedback: z.string().optional(),
  signalDescription: z.string().optional(),
  signalCategory: z.string().optional(),
  postAttackAnalysis: z.string().optional(),
  postAttackLabel: z.string().optional(),
  matchedRuleIds: z.array(z.string()).optional(),
  continualLearningAnalysis: z.string().optional(),
  routedAction: ContinualLearningRoutedActionSchema.optional(),
  signalEndpointId: z.string().optional(),
  submittedMemoryContent: z.string().optional(),
  endpointMemories: z.array(FpEndpointMemoryRecordSchema).optional(),
  applicationMemories: z.array(FpEndpointMemoryRecordSchema).optional(),
  applicationBehavior: FpApplicationBehaviorSchema.optional(),
  investigationChecks: FpInvestigationChecksSchema.optional(),
  isFalsePositive: z.boolean().optional(),
  findingIsPublished: z.boolean().optional(),
  createdAt: z.iso.datetime(),
});

export type FpFeedbackRow = z.infer<typeof FpFeedbackRowSchema>;

export const FpCategoryCountSchema = z.object({
  category: z.string(),
  count: z.number(),
  percent: z.number(),
});

export const FpSignalCategoryCountSchema = z.object({
  category: z.string(),
  count: z.number(),
  percent: z.number(),
});

export const FpSeverityCountSchema = z.object({
  severity: z.string(),
  count: z.number(),
  percent: z.number(),
});

export const FpDateCountSchema = z.object({
  date: z.string(),
  count: z.number(),
  withFeedback: z.number(),
  withoutFeedback: z.number(),
});

export const FpTenantCountSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string().optional(),
  count: z.number(),
});

export const FpSubmitterSchema = z.object({
  userId: z.string(),
  displayName: z.string(),
  count: z.number(),
});

export type FpSubmitter = z.infer<typeof FpSubmitterSchema>;

export const FpAnalyticsSchema = z.object({
  totals: z.object({
    rows: z.number(),
    uniqueSignals: z.number(),
    uniqueFindings: z.number().optional(),
    withFeedback: z.number(),
    withTrace: z.number(),
    uncategorizedCount: z.number(),
  }),
  byCategory: z.array(FpCategoryCountSchema),
  bySignalCategory: z.array(FpSignalCategoryCountSchema),
  bySeverity: z.array(FpSeverityCountSchema),
  byDate: z.array(FpDateCountSchema),
  byTenant: z.array(FpTenantCountSchema),
  bySubmitter: z.array(FpSubmitterSchema),
});

export const FpFeedbackPageSchema = z.object({
  items: z.array(FpFeedbackRowSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  truncated: z.boolean().optional(),
});

export const FpReportModeSchema = z.enum(['daily', 'weekly']);
export type FpReportMode = z.infer<typeof FpReportModeSchema>;

export const FpReportMetaSchema = z.object({
  date: z.string(),
  mode: FpReportModeSchema,
  s3Key: z.string(),
});

export type FpReportMeta = z.infer<typeof FpReportMetaSchema>;

export const FpReportListQuerySchema = z.object({});

export const FpReportsListSchema = z.object({
  reports: z.array(FpReportMetaSchema),
});

export const FpReportQuerySchema = z.object({
  date: z.string(),
  mode: FpReportModeSchema,
});

export const FpReportContentSchema = z.object({
  date: z.string(),
  mode: FpReportModeSchema,
  markdown: z.string(),
});

export const FpRecommendationCategoryCountSchema = z.object({
  category: FpFailureCategorySchema,
  count: z.int().min(1),
});

export const FpRecommendationSchema = z.object({
  id: z.string(),
  title: z.string(),
  targetComponent: z.string(),
  rationale: z.string(),
  confidence: z.number().min(0).max(1),
  evidenceSignalIds: z.array(z.string()),
  categoryBreakdown: z.array(FpRecommendationCategoryCountSchema),
  mode: FpReportModeSchema,
  date: z.string(),
});

export type FpRecommendation = z.infer<typeof FpRecommendationSchema>;

export const FpRecommendationsContentSchema = z.object({
  date: z.string(),
  mode: FpReportModeSchema,
  recommendations: z.array(FpRecommendationSchema),
});

export const FpRecommendationReportMetaSchema = z.object({
  date: z.string(),
  mode: FpReportModeSchema,
  s3Key: z.string(),
});

export type FpRecommendationReportMeta = z.infer<
  typeof FpRecommendationReportMetaSchema
>;

export const FpRecommendationReportsListSchema = z.object({
  reports: z.array(FpRecommendationReportMetaSchema),
});

export const FpRecommendationsQuerySchema = z.object({
  date: z.string(),
  mode: FpReportModeSchema,
});

export const FnKindSchema = z.enum([
  'status_reconfirmed',
  'finding_after_disproven',
]);

export type FnKind = z.infer<typeof FnKindSchema>;

export const FnAnalyticsQuerySchema = z.object({
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  tenantId: z.string().optional(),
  tenantIds: OptionalCommaSeparatedTenantIdsSchema,
  targetType: FpTargetTypeSchema.optional(),
  kind: FnKindSchema.optional(),
  signalCategory: z.string().optional(),
  hasTesterFeedback: OptionalBooleanQueryParamSchema,
  submitterId: z.string().optional(),
});

export const FnListQuerySchema = FnAnalyticsQuerySchema.extend({
  search: z.string().max(200).optional(),
  page: z.coerce.number().int().min(1).optional().prefault(1),
  pageSize: z.coerce.number().int().min(1).max(100).optional().prefault(20),
  fullExport: BooleanQueryParamDefaultFalseSchema,
});

export const FnRowSchema = z.object({
  signalId: z.string(),
  applicationId: z.string(),
  tenantId: z.string(),
  tenantName: z.string().optional(),
  targetType: FpTargetTypeSchema.optional(),
  signalCategory: z.string().optional(),
  kind: FnKindSchema,
  disprovenAt: z.iso.datetime(),
  confirmedAt: z.iso.datetime().optional(),
  findingId: z.string().optional(),
  findingTitle: z.string().optional(),
  findingStatus: z.string().optional(),
  findingPublishedAt: z.iso.datetime().optional(),
  investigationChecks: FpInvestigationChecksSchema.optional(),
  feedback: z.string().optional(),
  feedbackSubmitterId: z.string().optional(),
  feedbackSubmitterName: z.string().optional(),
});

export type FnRow = z.infer<typeof FnRowSchema>;

export const FnKindCountSchema = z.object({
  kind: FnKindSchema,
  count: z.number(),
});

export const FnAnalyticsSchema = z.object({
  totals: z.object({
    rows: z.number(),
    uniqueSignals: z.number(),
  }),
  byKind: z.array(FnKindCountSchema),
  bySignalCategory: z.array(FpSignalCategoryCountSchema),
  byDate: z.array(
    z.object({
      date: z.string(),
      count: z.number(),
    }),
  ),
  byTenant: z.array(FpTenantCountSchema),
  bySubmitter: z.array(FpSubmitterSchema),
});

export const FnPageSchema = z.object({
  items: z.array(FnRowSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  truncated: z.boolean().optional(),
});

export const DeduplicateSignalsBulkRequestSchema = z.object({
  applicationId: z.string(),
});

export const DeduplicateSignalsBulkResponseSchema = z.object({
  success: z.boolean(),
});

export const BusinessContextCapabilityStatusSchema = z.enum([
  'Answered',
  'Partial',
  'Unanswered',
]);

export const BusinessContextCapabilityQuestionItemSchema = z.object({
  questionId: z.string(),
  topic: z.string(),
  question: z.string(),
  whyItMatters: z.string(),
  status: BusinessContextCapabilityStatusSchema,
  summary: z.string(),
  answer: z.string(),
  lastComputedAt: z.string().nullable(),
});

export const BusinessContextCapabilityApplicationSummarySchema = z.object({
  applicationId: z.string(),
  applicationName: z.string(),
  tenantId: z.string(),
  tenantName: z.string().nullish(),
  overallScore: z.number(),
  lastComputedAt: z.string().nullable(),
  capabilityAnsweredCount: z.number(),
  capabilityPartialCount: z.number(),
  capabilityTotalCount: z.number(),
});

export const BusinessContextCapabilityOverviewQuerySchema = z.object({
  tenantId: z.string().optional(),
  search: z.string().optional(),
  limit: z.coerce.number().min(1).max(200).optional().prefault(50),
  offset: z.coerce.number().min(0).max(50_000).optional().prefault(0),
});

export const BusinessContextCapabilityOverviewSchema = z.object({
  items: z.array(BusinessContextCapabilityApplicationSummarySchema),
  total: z.number(),
});

export const BusinessContextCapabilityDetailSchema = z.object({
  applicationId: z.string(),
  applicationName: z.string(),
  tenantId: z.string(),
  tenantName: z.string().nullish(),
  overallScore: z.number(),
  lastComputedAt: z.string().nullable(),
  questions: z.array(BusinessContextCapabilityQuestionItemSchema),
});

export type BusinessContextCapabilityQuestionItem = z.infer<
  typeof BusinessContextCapabilityQuestionItemSchema
>;
export type BusinessContextCapabilityApplicationSummary = z.infer<
  typeof BusinessContextCapabilityApplicationSummarySchema
>;
export type BusinessContextCapabilityOverview = z.infer<
  typeof BusinessContextCapabilityOverviewSchema
>;
export type BusinessContextCapabilityDetail = z.infer<
  typeof BusinessContextCapabilityDetailSchema
>;

export const ContentFilterListQuerySchema = z.object({
  tenantId: z.string().optional(),
  applicationId: z.string().optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
  agentName: z.string().optional(),
  stopReason: z.string().optional(),
  page: z.coerce.number().int().min(1).optional().prefault(1),
  pageSize: z.coerce.number().int().min(1).max(100).optional().prefault(25),
});

export const ContentFilterListItemSchema = z.object({
  id: z.string(),
  createdAt: z.iso.datetime(),
  tenantId: z.string(),
  tenantName: z.string().nullish(),
  applicationId: z.string(),
  applicationName: z.string().nullish(),
  threadId: z.string(),
  traceId: z.string().nullable(),
  signalId: z.string().nullable(),
  agentName: z.string(),
  stopReason: z.string(),
  requestId: z.string().nullable(),
  modelName: z.string().nullable(),
  alertDispatched: z.boolean(),
  humanMessagePreview: z.string(),
});

export const ContentFilterDetailSchema = z.object({
  id: z.string(),
  createdAt: z.iso.datetime(),
  tenantId: z.string(),
  tenantName: z.string().nullish(),
  applicationId: z.string(),
  applicationName: z.string().nullish(),
  threadId: z.string(),
  traceId: z.string().nullable(),
  signalId: z.string().nullable(),
  agentName: z.string(),
  serviceName: z.string(),
  stopReason: z.string(),
  provider: z.string(),
  requestId: z.string().nullable(),
  modelProvider: z.string().nullable(),
  modelName: z.string().nullable(),
  humanMessages: z.array(z.string()),
  correlationId: z.string().nullable(),
  flowId: z.string().nullable(),
  triggerSource: z.string().nullable(),
  alertDispatched: z.boolean(),
});

export const ContentFilterAnalyticsQuerySchema = z.object({
  tenantId: z.string().optional(),
  applicationId: z.string().optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
  agentName: z.string().optional(),
  stopReason: z.string().optional(),
});

export const ContentFilterDateCountSchema = z.object({
  date: z.string(),
  count: z.number(),
});

export const ContentFilterTenantCountSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string().nullish(),
  count: z.number(),
});

export const ContentFilterAnalyticsSchema = z.object({
  total: z.number(),
  byDate: z.array(ContentFilterDateCountSchema),
  byTenant: z.array(ContentFilterTenantCountSchema),
});

export type ContentFilterListQuery = z.infer<
  typeof ContentFilterListQuerySchema
>;
export type ContentFilterListItem = z.infer<typeof ContentFilterListItemSchema>;
export type ContentFilterDetail = z.infer<typeof ContentFilterDetailSchema>;
export type ContentFilterAnalyticsQuery = z.infer<
  typeof ContentFilterAnalyticsQuerySchema
>;
export type ContentFilterAnalytics = z.infer<
  typeof ContentFilterAnalyticsSchema
>;

export const CoPilotEvalScenarioSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  tags: z.array(z.string()).optional(),
  applicationId: z.string(),
  goal: z.string(),
  persona: z.string().nullable().optional(),
  initialMessage: z.string(),
  maxTurns: z.number(),
  modelSelection: z.string(),
  expectedToolCalls: z.array(z.string()).optional(),
  expectedGoalReached: z.boolean().optional(),
  expectedBehavior: z.string().nullable().optional(),
  groundTruthNotes: z.string().nullable().optional(),
  judgeModel: z.string().nullable().optional(),
  fixtures: z.record(z.string(), z.unknown()).nullable().optional(),
  driverScript: z
    .array(z.record(z.string(), z.unknown()))
    .nullable()
    .optional(),
  postRunAssertions: z
    .array(z.record(z.string(), z.unknown()))
    .nullable()
    .optional(),
  isActive: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const CoPilotEvalRunSchema = z.looseObject({
  id: z.string(),
  scenarioId: z.string(),
  status: z.string(),
  modelUsed: z.string(),
  codeVersion: z.string().nullable().optional(),
  overallScore: z.number().nullable().optional(),
  verdict: z.string().nullable().optional(),
  technicalFailure: z.boolean().optional(),
  chatId: z.string().nullable().optional(),
  langfuseSessionId: z.string().nullable().optional(),
  batchId: z.string().nullable().optional(),
  batchIndex: z.number().nullable().optional(),
  experimentId: z.string().nullable().optional(),
  datasetExperimentId: z.string().nullable().optional(),
  totalTurns: z.number().nullable().optional(),
  totalTokens: z.number().nullable().optional(),
  inputTokens: z.number().nullable().optional(),
  outputTokens: z.number().nullable().optional(),
  reasoningTokens: z.number().nullable().optional(),
  cacheReadTokens: z.number().nullable().optional(),
  cacheWriteTokens: z.number().nullable().optional(),
  totalCostUsd: z.number().nullable().optional(),
  totalLatencyMs: z.number().nullable().optional(),
  ttftP50Ms: z.number().nullable().optional(),
  ttftP95Ms: z.number().nullable().optional(),
  turnLatencyP50Ms: z.number().nullable().optional(),
  turnLatencyP95Ms: z.number().nullable().optional(),
  streamOpenP50Ms: z.number().nullable().optional(),
  streamOpenP95Ms: z.number().nullable().optional(),
  investigationSummary: z.string().nullable().optional(),
  failureReason: z.string().nullable().optional(),
  technicalFailureReason: z.string().nullable().optional(),
  guardrailsFired: z.number().nullable().optional(),
  hitlGateCount: z.number().nullable().optional(),
  askUserCount: z.number().nullable().optional(),
  toolCalls: z.array(z.string()).optional(),
  langfuseTraceIds: z.array(z.string()).optional(),
  transcriptS3Key: z.string().nullable().optional(),
  startedAt: z.iso.datetime().nullable().optional(),
  completedAt: z.iso.datetime().nullable().optional(),
  createdAt: z.iso.datetime().nullable().optional(),
  qualityScores: z
    .record(
      z.string(),
      z.object({
        score: z.number(),
        justification: z.string(),
      }),
    )
    .nullable()
    .optional(),
  objectiveScores: z
    .object({
      goalReached: z.boolean(),
      expectedToolsCalled: z.boolean(),
      gatesHandledCorrectly: z.boolean(),
      guardrailsHeld: z.boolean(),
      turnsEfficiency: z.number(),
      assertionsPassed: z.boolean().optional(),
      expectedBehaviorMet: z.boolean().nullable().optional(),
    })
    .nullable()
    .optional(),
  assertionResults: z
    .object({
      passed: z.boolean(),
      results: z.array(
        z.object({
          assertionIndex: z.number(),
          type: z.string(),
          passed: z.boolean(),
          message: z.string(),
        }),
      ),
    })
    .nullable()
    .optional(),
});

export const CoPilotEvalDashboardSchema = z.object({
  totalRuns: z.number(),
  passRate: z.number(),
  averageScore: z.number(),
  byModel: z.array(z.record(z.string(), z.unknown())),
  byCategory: z.array(z.record(z.string(), z.unknown())),
  scoreTrend: z.array(z.record(z.string(), z.unknown())),
});

export const CreateCoPilotEvalScenarioBodySchema = z.looseObject({
  name: z.string(),
  applicationId: z.string(),
  goal: z.string(),
  initialMessage: z.string(),
  category: z.string().optional(),
  tags: z.array(z.string()).optional(),
  expectedBehavior: z.string().optional(),
});

export const ImportCoPilotEvalScenariosBodySchema = z.object({
  applicationId: z.string(),
  category: z.string().optional(),
  rows: z
    .array(
      z.object({
        userPrompt: z.string(),
        expectedFlow: z.string().optional(),
        successCriteria: z.string().optional(),
      }),
    )
    .min(1),
});

export const ImportCoPilotEvalScenariosResultSchema = z.object({
  created: z.number(),
  scenarioIds: z.array(z.string()),
});

export const DraftCoPilotEvalScenarioBodySchema = z.object({
  description: z.string(),
  applicationId: z.string(),
});

export const CoPilotEvalScenarioDraftSchema = z.object({
  name: z.string(),
  goal: z.string(),
  initialMessage: z.string(),
  expectedBehavior: z.string(),
  category: z.string(),
  expectedToolCalls: z.array(z.string()),
});

export const COPILOT_EVAL_CSV_MAX_CHARS = 1_000_000;

export const ParseCoPilotEvalScenariosCsvBodySchema = z.object({
  applicationId: z.string(),
  csvText: z.string().min(1).max(COPILOT_EVAL_CSV_MAX_CHARS),
  category: z.string().optional(),
});

export const ParseCoPilotEvalScenariosCsvResultSchema = z.object({
  scenarios: z.array(CoPilotEvalScenarioDraftSchema),
});

export const ImportCoPilotEvalScenariosFromDraftsBodySchema = z.object({
  applicationId: z.string(),
  defaultCategory: z.string().optional(),
  scenarios: z.array(CoPilotEvalScenarioDraftSchema).min(1),
});

export const TriggerCoPilotEvalRunBodySchema = z.object({
  scenarioId: z.string(),
  runModel: CoPilotRunModelSchema.optional(),
  modelSelection: CoPilotRunModelSchema.optional(),
  agentModelOverrides: CoPilotAgentModelOverridesSchema.optional(),
  experimentId: z.string().optional(),
});

export const BulkTriggerCoPilotEvalRunBodySchema = z.object({
  scenarioId: z.string(),
});

export const BatchTriggerCoPilotEvalRunBodySchema = z.object({
  scenarioId: z.string(),
  count: z.coerce.number().int().min(1).max(20),
  runModel: CoPilotRunModelSchema.optional(),
  modelSelection: CoPilotRunModelSchema.optional(),
  agentModelOverrides: CoPilotAgentModelOverridesSchema.optional(),
  name: z.string().optional(),
  experimentGroupId: z.string().optional(),
});

export const CreateCoPilotEvalExperimentBodySchema =
  BatchTriggerCoPilotEvalRunBodySchema;

export const CoPilotEvalExperimentSchema = z.looseObject({
  id: z.string(),
  scenarioId: z.string(),
  experimentGroupId: z.string().nullable().optional(),
  datasetId: z.string().nullable().optional(),
  datasetExperimentId: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  runModel: z.string(),
  agentModels: CoPilotAgentModelsConfigSchema.nullable().optional(),
  requestedCount: z.number(),
  triggeredBy: z.string(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  scenario: z
    .object({
      name: z.string().optional(),
      applicationId: z.string().optional(),
      category: z.string().nullable().optional(),
    })
    .optional(),
});

export const CoPilotEvalScenarioStatsSchema = z.object({
  scenarioId: z.string(),
  runCount: z.number(),
  passRate: z.number(),
  averageOverallScore: z.number(),
  lastRunAt: z.iso.datetime().nullable(),
  qualityDimensionAverages: z.record(z.string(), z.number()),
  objectiveScoreRates: z.record(z.string(), z.number()),
});

export const CoPilotEvalScoreAggregateSchema = z.object({
  mean: z.number(),
  std: z.number(),
  min: z.number(),
  max: z.number(),
});

export const CoPilotEvalBatchStatsSchema = z.object({
  batchId: z.string(),
  runCount: z.number(),
  modelUsed: z.string(),
  overallScore: CoPilotEvalScoreAggregateSchema,
  qualityDimensions: z.record(z.string(), CoPilotEvalScoreAggregateSchema),
  objectiveRates: z.record(z.string(), z.number()),
});

export const CoPilotEvalExperimentStatsSchema =
  CoPilotEvalBatchStatsSchema.extend({
    experimentId: z.string(),
    scenarioId: z.string(),
    scenarioName: z.string(),
    name: z.string().nullable().optional(),
    runModel: z.string(),
    requestedCount: z.number(),
    triggeredBy: z.string(),
    createdAt: z.iso.datetime(),
  }).omit({ batchId: true });

export const ExportCoPilotEvalRunsQuerySchema = z.object({
  scenarioId: z.string().optional(),
  batchId: z.string().optional(),
  experimentId: z.string().optional(),
  experimentGroupId: z.string().optional(),
  experimentIds: z.string().optional(),
  status: z.string().optional(),
  verdict: z.string().optional(),
  modelUsed: z.string().optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
  format: z.enum(['csv', 'batch-csv']).optional(),
});

export const CoPilotEligibleModelSchema = z.object({
  slug: z.string(),
  label: z.string(),
  provider: z.enum([
    'anthropic',
    'fireworks',
    'google',
    'meta',
    'nvidia',
    'openai',
    'other',
  ]),
  family: z.enum([
    'sonnet',
    'opus',
    'haiku',
    'gemini',
    'gemma',
    'gpt',
    'llama',
    'nemotron',
    'fireworks',
    'other',
  ]),
  isThinking: z.boolean(),
  configured: z.boolean().default(true),
});

export const CoPilotRunModelsSchema = z.object({
  presets: z.array(CoPilotRunModelSchema),
  models: z.array(CoPilotEligibleModelSchema),
});

export const ResolveCoPilotAgentModelsBodySchema = z.object({
  runModel: CoPilotRunModelSchema,
  agentModelOverrides: CoPilotAgentModelOverridesSchema.optional(),
});

export const CoPilotEvalDatasetSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  scenarioIds: z.array(z.string()),
  createdBy: z.string(),
  isActive: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const CreateCoPilotEvalDatasetBodySchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  scenarioIds: z.array(z.string()).default([]),
});

export const UpdateCoPilotEvalDatasetBodySchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  scenarioIds: z.array(z.string()).optional(),
});

export const ImportCoPilotEvalDatasetCsvBodySchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  applicationId: z.string(),
  category: z.string().optional(),
  rows: ImportCoPilotEvalScenariosBodySchema.shape.rows,
});

export const ImportCoPilotEvalDatasetCsvAiBodySchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  applicationId: z.string(),
  category: z.string().optional(),
  csvText: z.string().min(1).max(COPILOT_EVAL_CSV_MAX_CHARS),
});

export const ImportCoPilotEvalDatasetCsvResultSchema = z.object({
  dataset: CoPilotEvalDatasetSchema,
  created: z.number(),
  scenarioIds: z.array(z.string()),
});

export const DatasetExperimentConfigSchema = z.object({
  runModel: CoPilotRunModelSchema,
  count: z.coerce.number().int().min(1).max(20),
  agentModelOverrides: CoPilotAgentModelOverridesSchema.optional(),
});

export const CoPilotEvalDatasetExperimentSchema = z.object({
  id: z.string(),
  datasetId: z.string(),
  name: z.string().nullable().optional(),
  configs: z.array(DatasetExperimentConfigSchema),
  createdBy: z.string(),
  createdAt: z.iso.datetime(),
  dataset: z
    .object({
      name: z.string().optional(),
      scenarioIds: z.array(z.string()).optional(),
    })
    .optional(),
});

export const CreateCoPilotEvalDatasetExperimentBodySchema = z.object({
  datasetId: z.string(),
  name: z.string().optional(),
  configs: z.array(DatasetExperimentConfigSchema).min(1),
});

export const CoPilotEvalDatasetModelStatsSchema = z.object({
  runModel: z.string(),
  scenarioCount: z.number(),
  runCount: z.number(),
  passRate: CoPilotEvalScoreAggregateSchema,
  overallScore: CoPilotEvalScoreAggregateSchema,
  qualityDimensions: z.record(z.string(), CoPilotEvalScoreAggregateSchema),
  objectiveRates: z.record(z.string(), z.number()),
  avgTurns: CoPilotEvalScoreAggregateSchema,
  avgToolCalls: CoPilotEvalScoreAggregateSchema,
  avgLatencySeconds: CoPilotEvalScoreAggregateSchema,
  avgCostUsd: CoPilotEvalScoreAggregateSchema,
  avgTokens: CoPilotEvalScoreAggregateSchema,
});

export const CoPilotEvalDatasetExperimentStatsSchema = z.object({
  datasetExperimentId: z.string(),
  datasetId: z.string(),
  datasetName: z.string(),
  name: z.string().nullable().optional(),
  scenarioCount: z.number(),
  childExperimentCount: z.number(),
  modelStats: z.array(CoPilotEvalDatasetModelStatsSchema),
});

export const CoPilotEvalMetaActionItemSchema = z.object({
  title: z.string(),
  evidence: z.string(),
  action: z.string(),
  priority: z.enum(['high', 'medium', 'low']),
});

export const CoPilotEvalMetaAnalysisSchema = z.object({
  headline: z.string(),
  summary: z.string(),
  modelComparison: z.array(
    z.object({
      runModel: z.string(),
      vsPeers: z.string(),
      preferWhen: z.string(),
    }),
  ),
  harnessActionItems: z.array(CoPilotEvalMetaActionItemSchema),
  evaluationSystemActionItems: z.array(CoPilotEvalMetaActionItemSchema),
});

export const CoPilotEvalDatasetExperimentDetailSchema = z.object({
  experiment: z.object({
    id: z.string(),
    datasetId: z.string(),
    name: z.string().nullable(),
    configs: z.array(z.unknown()),
    createdBy: z.string(),
    createdAt: z.iso.datetime(),
    metaAnalysis: CoPilotEvalMetaAnalysisSchema.nullable(),
    metaAnalysisAt: z.iso.datetime().nullable(),
    dataset: z.object({
      name: z.string(),
      scenarioIds: z.array(z.string()),
    }),
  }),
  scenarios: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      goal: z.string(),
      expectedBehavior: z.string().nullable(),
    }),
  ),
  stats: z.object({
    scenarioCount: z.number(),
    childExperimentCount: z.number(),
    modelStats: z.array(CoPilotEvalDatasetModelStatsSchema),
  }),
  statusBreakdown: z.record(z.string(), z.number()),
  technicalFailureCount: z.number(),
  technicalFailureReasons: z.array(
    z.object({
      reason: z.string(),
      count: z.number(),
    }),
  ),
  failureReasons: z.array(
    z.object({
      reason: z.string(),
      count: z.number(),
    }),
  ),
  runs: z.array(
    z.object({
      id: z.string(),
      scenarioId: z.string(),
      experimentId: z.string().nullable(),
      status: z.string(),
      modelUsed: z.string(),
      overallScore: z.number().nullable(),
      verdict: z.string().nullable(),
      goalReached: z.boolean().nullable(),
      totalTurns: z.number().nullable(),
      totalTokens: z.number().nullable(),
      totalCostUsd: z.number().nullable(),
      totalLatencyMs: z.number().nullable(),
      toolCalls: z.array(z.string()),
      technicalFailure: z.boolean(),
      technicalFailureReason: z.string().nullable(),
      failureReason: z.string().nullable(),
      investigationSummary: z.string().nullable(),
    }),
  ),
});

export const CoPilotEvalDatasetExperimentMetaAnalysisResponseSchema = z.object({
  metaAnalysis: CoPilotEvalMetaAnalysisSchema.nullable(),
  metaAnalysisAt: z.iso.datetime().nullable(),
});

export const CoPilotEvalDatasetExperimentMetaAnalysisGenerateResponseSchema =
  z.object({
    metaAnalysis: CoPilotEvalMetaAnalysisSchema,
    metaAnalysisAt: z.iso.datetime(),
  });
